/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Home — Main contract editor for closet design jobs.
 *
 * Data hierarchy:
 *   Job (parent) → client info, billing address, file URLs, financial summary
 *     └── Closet Form (child) → one closet's design: collection, doors,
 *         drawers, rods, moulding, accessories (racks/baskets/hooks/hampers),
 *         countertop, drilling, notes
 *
 * Workflow:
 *   search/select job → fill client details → create job →
 *   select closet form → fill design → fill financial → preview PDF → send for sign
 *
 * State sections (Ctrl+F "SECTION:" to jump between them):
 *   Client & Job Details | Job Site Address | Billing Address
 *   Collection & Color | Doors | Special Doors | Drawers | Rods | Moulding
 *   Baseboard & Other Details | Accessories (Racks/Baskets/Hooks/Hampers)
 *   Financial Summary | Payment & Job State | PDF & File Upload
 */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { logger } from "../../utilities/logger";
import { usePDFGenerator } from "../../hooks/usePDFGenerator";
import { useNotification } from "../../hooks/useNotification";
import { useFinancialSummary } from "../../hooks/useFinancialSummary";
import { useAddressFields } from "../../hooks/useAddressFields";
import { useAccessoryLists } from "../../hooks/useAccessoryLists";
import { useClosetDesign } from "../../hooks/useClosetDesign";
//import DecoSelectShared from "../../components/DecoSelect";
import YesNoRadioComponent from "../../components/YesNoRadio";
//import DecoAutocompleteComponent from "../../components/DecoAutocomplete";
import DecoOptionsComponent from "../../components/DecoOptions";
import GripOptionsComponent from "../../components/GripOptions";
import SpecialDoorOptionsComponent from "../../components/SpecialDoorOptions";
import RodOptionsComponent from "../../components/RodOptions";
import CountertopOptionsComponent from "../../components/CountertopOptions";
import DynamicTabsForm from "../../components/DynamicTabsForm";
import RackRowFields, { createEmptyRackRow } from "../../components/RackComponent";
import BasketRowFields, { createEmptyBasketRow } from "../../components/BasketComponent";
import HookRowFields, { createEmptyHookRow } from "../../components/HookComponent";
import HamperRowFields, { createEmptyHamperRow } from "../../components/HamperComponent";
import {
  cuttingAreaOptions, tearOutOptions,
  colorOptions, gripoptions, topoptions, bottomoptions,
  collectionOptions,
} from "../../constants/closetOptions";
import home from "../../assests/images/home (3).png";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,


  TextField,
  Button,
  Select,
  MenuItem,
  //Switch,
  Typography,
  Grid2,
  Grid,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  Autocomplete,
  RadioGroup,
  CircularProgress,
  Link,
  //Tabs,
  //Tab,
  //IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AppNavBar from "../../components/Navbar";
import AppTheme from "../../theme/AppTheme";
import { GeneratePDFService } from "../../services/GeneratePDFService";
import { LocalStorageService } from "../../services/LocalStorageService";
import moment from "moment";
import Draggable from "react-draggable";
import { LoginService } from "../../services/LoginService";
import { AzureBlobStorageService } from "../../services/AzureBlobStorageService";
import { ContainerClient } from "@azure/storage-blob";
import Loader from "../../components/Loader/Loader";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";


const errorPrefix = "⚠️ ";

// Local aliases matching old names so JSX references require no changes
const YesNoRadio = YesNoRadioComponent;

const DraggableHome = () => {
  const nodeRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });

  const handleStart = (e, data) => {
    setDragStartPosition({ x: data.x, y: data.y });
  };

  const handleStop = (e, data) => {
    const deltaX = Math.abs(data.x - dragStartPosition.x);
    const deltaY = Math.abs(data.y - dragStartPosition.y);
    const totalMovement = deltaX + deltaY;

    if (totalMovement < 5) {
      // Considered a click
      window.location.reload();
    } else {
      // Considered a drag
      setPosition({ x: data.x, y: data.y });
    }
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStart={handleStart}
      onStop={handleStop}
      enableUserSelectHack={false}
    >
      <div
        ref={nodeRef}
        style={{
          position: "fixed", // ✅ Makes it float relative to the viewport
          bottom: 20, // ✅ Starting Y position
          right: 20, // ✅ Starting x position
          zIndex: 1000,
          touchAction: "none",
        }}
      >
        <img
          src={home}
          alt="Home"
          draggable={false}
          style={{
            width: 60,
            height: 60,
            cursor: "grab",
            userSelect: "none",
            touchAction: "none",
          }}
        />
      </div>
    </Draggable>
  );
};
const StyledCard = styled(Card)({
  width: "95%",
  position: "relative",
  //borderRadius: 8,
  //boxShadow: 3,
  backgroundColor: "#faf9f7",
  //border: "2px solid #faf9f7",
});
// Stable, memoized so typing never causes a remount/focus loss.
// Defined OUTSIDE Home so its identity never changes between renders.
const PricingTextField = React.memo(function PricingTextField({
  field,
  touched,
  onBlur,
  multiline = false,
  rows = 4,
}) {
  const hasError = touched[field.key] && field.error;
  return (
    <FormControl fullWidth required={!!field.label} size="small" error={hasError}>
      {field.label && <FormLabel>{field.label}</FormLabel>}
      <TextField
        variant="outlined"
        value={field.value}
        onChange={field.onChange}
        onBlur={() => onBlur(field.key)}
        error={hasError}
        helperText={hasError ? field.helperText : ""}
        multiline={multiline}
        rows={multiline ? rows : undefined}
      />
    </FormControl>
  );
});

const Home = () => {
  const { notify, NotificationSnackbar } = useNotification();



  // Touched state for validation feedback
  const [touched, setTouched] = useState({});
  const handleBlur = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Client & Job Details
  // Saved to: parent job record (jobid, clientName, etc.)
  // ─────────────────────────────────────────────────────────────────────────────
  const [logId, setLogId] = useState(0);
  const [jobID, setJobID] = useState("");
  const [clientName, setClientName] = useState("");
  const [designerName, setDesignerName] = useState("");
  const [clienttelephone, setClientTelephone] = useState("");
  const [clientemail, setClientEmail] = useState("");
  const [cuttingarea, setDesignatedCuttingArea] = useState("");
  const [closetsFormCount, setClosetsFormCount] = useState(0);
  const [originalClosetsFormCount, setOriginalClosetsFormCount] = useState(0);
  const [isTotalClosetsDisabled, setIsTotalClosetsDisabled] = useState(false);



  const [tearout, setTearOut] = useState("");
  const [validateJobIdLength, setValidateJobIdLength] = useState(0);
  const [isNew, setIsNew] = useState(false);
  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Job Site Address + Billing Address — state managed by useAddressFields
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    address, setAddress,
    city, setCity,
    province, setProvince,
    postalCode, setPostalCode,
    jobsiteStreetName, setJobSiteStreetName,
    projectname, setProjectName,
    hasCondos, setHasCondos,
    housenumber, sethousenumber,
    floorInfo, setFloorInfo,
    longWalk, setLongWalk,
    standardDrill, setStandardDrill,
    billingAddress, setBillingAddress,
    billingCity, setBillingCity,
    billingProvince, setBillingProvince,
    billingPostalCode, setBillingPostalCode,
    billingStreetName, setBillingStreetName,
    isSameAsbillingAddress, setIsSameAsbillingAddress,
  } = useAddressFields({ jobID });

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Collection, Doors, Drawers, Rods, Moulding, Baseboard, Countertop
  // Managed by useClosetDesign — includes derived decoStyleMenuItems, moldingMenuItems
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    collectionList, setCollectionList,
    selectedColor, setSelectedColor,
    closetCollection, setClosetCollection,
    isSameAsCollectionColor, setIsSameAsCollectionColor,
    doorEnabled, setDoorEnabled,
    doorQuantity, setDoorQuantity,
    doorDecoStyle, setDoorDecoStyle,
    doorSelectedSeries, setDoorSelectedSeries,
    doorSelectedVariant, setDoorSelectedVariant,
    doorColor, setDoorColor,
    doorgripStyle, setDoorgripStyle,
    doorgripSelectedSeries, setDoorgripSelectedSeries,
    doorgripSelectedSize, setDoorgripSelectedSize,
    doorgripColor, setDoorgripColor,
    specialDoorEnabled, setSpecialDoorEnabled,
    specialDoorgripStyle, setSpecialDoorgripStyle,
    specialDoorgripSelectedSeries, setSpecialDoorgripSelectedSeries,
    specialDoorgripSelectedSize, setSpecialDoorgripSelectedSize,
    specialDoorgripColor, setSpecialDoorgripColor,
    specialDoorQuantity, setSpecialDoorQuantity,
    specialDoorType, setSpecialDoorType,
    specialDoorSize, setSpecialDoorSize,
    specialDoorHeight, setSpecialDoorHeight,
    specialDoorInsert, setSpecialDoorInsert,
    specialDoorFrame, setSpecialDoorFrame,
    specialDoorEdge, setSpecialDoorEdge,
    setSpecialDoorSectionError,
    specialDoorFieldErrors, setSpecialDoorFieldErrors,
    closetColl, setClosetColl,
    isSameAsCollColor, setIsSameAsCollColor,
    drawerEnabled, setDrawerEnabled,
    drawerQuantity, setDrawerQuantity,
    drawerDecoStyle, setDrawerDecoStyle,
    drawerSelectedSeries, setDrawerSelectedSeries,
    drawerSelectedVariant, setDrawerSelectedVariant,
    drawerColor, setDrawerColor,
    frontclosetColl, setFrontClosetColl,
    isFrontSameAsCollColor, setIsFrontSameAsCollColor,
    drawerFrontColor, setDrawerFrontColor,
    drawergripStyle, setDrawergripStyle,
    drawergripSelectedSeries, setDrawergripSelectedSeries,
    drawergripSelectedSize, setDrawergripSelectedSize,
    drawergripColor, setDrawergripColor,
    rodEnabled, setRodEnabled,
    rodQuantity, setRodQuantity,
    rodStyle, setRodStyle,
    rodColor, setRodColor,
    mouldingStyle, setMouldingStyle,
    noTopFinish, setNoTopFinish,
    moulding1Style, setMoulding1Style,
    nobottomFinish, setNoBottomFinish,
    baseboardRemove, setBaseboardRemove,
    baseboardNotch, setBaseboardNotch,
    backing, setBacking,
    drilling, setDrilling,
    drillingtxt, setDrillingtxt,
    drillingChangeRef,
    drillingNotesError, setDrillingNotesError,
    handleDrillingChangeNotes,
    countertopEnabled, setCountertopEnabled,
    countertopType, setCountertopType,
    countertopColor, setCountertopColor,
    countertopCollection, setCountertopCollection,
    additionalNotes, setAdditionalNotes,
    decoStyleMenuItems,
    moldingMenuItems,
  } = useClosetDesign();
  const [kitchenSpacesEnabled, setKitchenSpacesEnabled] = useState(undefined);
  // ---- Kitchen Pricing Sheet Fields ----
  const [cabinetConstruction, setCabinetConstruction] = useState("");

  const [doorStyle, setDoorStyle] = useState("");
  const [doorMaterial, setDoorMaterial] = useState("");
  const [doorFinish, setDoorFinish] = useState("");
  const [finishEndsDummyDoors, setFinishEndsDummyDoors] = useState("");
  const [finishEndsFlatPanels, setFinishEndsFlatPanels] = useState("");
  const [finishEndsColumns, setFinishEndsColumns] = useState("");
  const [finishInteriorUppers, setFinishInteriorUppers] = useState("");

  const [drawerBoxesDetail, setDrawerBoxesDetail] = useState("");
  const [glidesDetail, setGlidesDetail] = useState("");
  const [openingHardwareDetail, setOpeningHardwareDetail] = useState("");
  const [crownSystemDetail, setCrownSystemDetail] = useState("");
  const [faciaPanelDetail, setFaciaPanelDetail] = useState("");
  const [decorRailDetail, setDecorRailDetail] = useState("");
  const [lightValanceDetail, setLightValanceDetail] = useState("");
  const [undermountPanelDetail, setUndermountPanelDetail] = useState("");

  const [accessoriesDetail, setAccessoriesDetail] = useState("");
  const [kitchenSpecialDetails, setKitchenSpecialDetails] = useState("");
  const [customCanopyDetail, setCustomCanopyDetail] = useState("");
  const [appliancesDoorPanelsDetail, setAppliancesDoorPanelsDetail] = useState("");
  const [hingesDetail, setHingesDetail] = useState("");
  const [glassTypeDetail, setGlassTypeDetail] = useState("");
  const [counterTopsDetail, setCounterTopsDetail] = useState("");
  const [electricalDetail, setElectricalDetail] = useState("");
  const [puckLightsDetail, setPuckLightsDetail] = useState("");
  const [plumbingDetail, setPlumbingDetail] = useState("");
  const [deliveryInstallationDetail, setDeliveryInstallationDetail] = useState("");

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Accessories — Racks, Baskets, Hooks, Hampers — managed by useAccessoryLists
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    rackEnabled, setRackEnabled,
    rackLists, setRackLists,
    activeRackTab, setActiveRackTab,
    rackFieldErrors, setRackFieldErrors,
    basketEnabled, setBasketEnabled,
    basketQuantity, //setBasketQuantity,
    baskettype, //setBasketType,
    basketliner, //setBasketLiner,
    basketsize, //setBasketSize,
    basket, //setBasket,
    basketLists, setBasketLists,
    activeBasketTab, setActiveBasketTab,
    basketFieldErrors, setBasketFieldErrors,
    //handleBasketChange, handleAddBasketRow, handleRemoveBasketRow,
    hooksEnabled, setHooksEnabled,
    hooktype, //setHookType,
    hookcolour, //setHookColour,
    hookspercleatQuantity, //setHooksPerCleatQuantity,
    cleatQuantity, //setCleatQuantity,
    hooksLists, setHooksLists,
    activeHooksTab, setActiveHooksTab,
    hookFieldErrors, setHookFieldErrors,
    //handleHooksChange, handleAddHooksRow, handleRemoveHooksRow,
    hamperEnabled, setHamperEnabled,
    hamperQuantity, //setHamperQuantity,
    hampertype, //setHamperType,
    hampercode, //setHamperCode,
    hampercolour,// setHamperColour,
    hampersize, //setHamperSize,
    hamperdeco, //setHamperDeco,
    hamperseries, //setHamperSeries,
    hampervariant, //setHamperVariant,
    hamperLists, setHamperLists,
    activeHamperTab, setActiveHamperTab,
    hamperFieldErrors, setHamperFieldErrors,
    // handleHamperChange, handleAddHamperRow, handleRemoveHamperRow,
  } = useAccessoryLists();

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Financial Summary — state/effects/handler managed by useFinancialSummary
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: Payment & Job State
  // Saved to: childForm.paymentDetail (depositePer, deposite, balanceDue, method, chequeNumber)
  // Also: UI flags controlling which panels are visible
  // ─────────────────────────────────────────────────────────────────────────────
  const [jobids, setJobIds] = useState([]);
  const [jobIdValue, setJobIdValue] = useState(null);
  const [isJobDetailViewOpen, setIsJobDetailViewOpen] = useState("none");
  const [isJobFilterViewOpen, setIsJobFilterViewOpen] = useState("show");

  const [visibleSubmit, setVisibleSubmit] = useState("show");
  const [visibleClosetsForm, setVisibleClosetsForm] = useState("none");
  const [optionJobIds, setOptionJobIds] = useState([]);
  const [selectedJobIdOption, setSelectedJobIdOption] = useState("");
  const [visibleFinancialSummary, setVisibleFinancialSummary] = useState(false);
  const [parentClosetsForm, setParentClosetsForm] = useState({});
  const [closetDisabled, setClosetDisabled] = useState(false);
  const [pdfEventType, setPdfEventType] = useState("");
  const [pdfURL, setPdfURL] = useState("");
  const [pdfCreated, setPdfCreated] = useState("");
  const [pdfDeclined, setPdfDeclined] = useState("");
  const [pdfCompleted, setPdfCompleted] = useState("");
  // After line 458:  const [pdfEventType, setPdfEventType] = useState("");
  const [hasChangesAfterSend, setHasChangesAfterSend] = useState(false);
  const [hasClosetForms, setHasClosetForms] = useState(false);
  const [hasKitchenForms, setHasKitchenForms] = useState(false);

  // Looks at every child form actually saved under this job and tells you
  // which types exist. Used to decide which financial block(s) to show.
  const computeFormTypeFlags = async (jobIdToUse) => {
    if (!jobIdToUse) return { hasCloset: false, hasKitchen: false };
    try {
      const allChildren = await GeneratePDFService.GetChildContent(jobIdToUse);
      const children = allChildren.filter((item) => {
        const extractedJobId = String(item.jobid).split("-")[0];
        return extractedJobId === String(jobIdToUse);
      });

      let hasCloset = false;
      let hasKitchen = false;
      children.forEach((item) => {
        if (!item.jsoncontent) return;
        try {
          const parsed = JSON.parse(item.jsoncontent);
          if (parsed.kitchenSpacesEnabled === true) hasKitchen = true;
          else hasCloset = true;
        } catch (e) {
          // skip unparsable entries
        }
      });
      return { hasCloset, hasKitchen };
    } catch (err) {
      return { hasCloset: false, hasKitchen: false };
    }
  };


  const [designerNames, setDesignerNames] = useState([]);
  const [selectedDesignerName, setSelectedDesignerName] = useState("");
  const isNewJobID = useRef(false); //For new contract
  const userRole = LocalStorageService.getRole();
  const [templateId, setTemplateId] = useState(0);

  const [designFileURL, setDesignFileURL] = useState("");
  const [pricePresentationFileURL, setPricePresentationFileURL] = useState("");

  /*const {
    subtotal, setSubtotal,
    hst, setHst,
    total, setTotal,
    deposit, setDeposit,
    depositOption, setDepositOption,
    progressPayment, setProgressPayment,
    balanceDue, setBalanceDue,
    setLastEdited,
    statusCard, setStatusCard,
    statusCardNumber, setStatusCardNumber,
    paymentMethod, setPaymentMethod,
    chequeNumber, setChequeNumber,
    paymentDetailsAdded, setPaymentDetailsAdded,
    handlePaymentDetails,
  } = useFinancialSummary({ jobID, parentClosetsForm, designFileURL, pricePresentationFileURL, notify, kitchenSpacesEnabled });*/
  const {
    closet: closetFinance,
    kitchen: kitchenFinance,
    statusCard, setStatusCard,
    statusCardNumber, setStatusCardNumber,
    paymentDetailsAdded, setPaymentDetailsAdded,
    handlePaymentDetails,
  } = useFinancialSummary({
    jobID,
    parentClosetsForm,
    designFileURL,
    pricePresentationFileURL,
    notify,
    hasClosetForms,
    hasKitchenForms,
  });

  const [showLoading, setShowLoading] = useState("none");

  const [shouldProcessEvent, setShouldProcessEvent] = useState(false);
  const [Loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // SECTION: PDF & File Upload State
  // Tracks PDF event status, generated blob URL, and uploaded file references
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (doorgripSelectedSeries === "Supplied By Customer") {
      setDoorgripColor("");
      setDoorgripSelectedSize("");
    }
  }, [doorgripSelectedSeries]);
  // ── EFFECT: PDF event processing (send/resend/signed/declined flow) ──
  useEffect(() => {
    if (!shouldProcessEvent) return;
    const processEvent = async () => {
      try {
        // ── Case 1: submission.created + changes exist ──
        if (pdfEventType === "submission.created" && hasChangesAfterSend) {
          const wantUpdate = window.confirm(
            "The contract has been sent out for signature. Do you still want to send it again?"
          );
          if (!wantUpdate) {
            // alert("The previous contract is still pending signature. Please check your inbox.");
            return;
          }
          const confirmSend = window.confirm(
            "Are you sure you want to send a second contract for this job? Press cancel to abort"
          );
          if (!confirmSend) {
            //alert("The previous contract is still pending signature. Please check your inbox.");
            return;
          }
          // User confirmed both steps — send updated contract
          await handleSubmit(false);
          setHasChangesAfterSend(false);
          return;
        }

        // ── Case 2: submission.created + NO changes ──
        if (pdfEventType === "submission.created") {
          notify(`This contract has already been sent for signature on ${pdfCreated}.`, "warning");
          return;
        }

        // ── Case 3: submission.completed (signed) ──
        if (pdfEventType === "submission.completed") {
          const ok = window.confirm(
            `The contract has already been signed on ${pdfCompleted}. Do you want to send it again for signature?`
          );
          if (!ok) return;
          const confirmSend = window.confirm(
            "Are you sure you want to send a second contract for this job? Press cancel to abort"
          );
          if (!confirmSend) {
            //alert("The previous contract is still pending signature. Please check your inbox.");
            return;
          }
        }

        // ── Case 4: form.declined ──
        if (pdfEventType === "form.declined") {
          const ok = window.confirm(
            `Client declined signature request on ${pdfDeclined}. Do you want to send it again for signature?`
          );
          if (!ok) return;
          const confirmSend = window.confirm(
            "Are you sure you want to send a second contract for this job? Press cancel to abort"
          );
          if (!confirmSend) return;
        }

        // Default — send the contract
        await handleSubmit(false);
        setHasChangesAfterSend(false);
      } finally {
        setShouldProcessEvent(false);
        setLoading(false);
        setShowLoading("none");
      }
    };


    processEvent();
  }, [shouldProcessEvent, pdfEventType, hasChangesAfterSend]);

  // ── EFFECT: Designer auto-assign from jobID ──
  useEffect(() => {
    async function assignDesigner() {
      if (isNewJobID.current) {
        return; // skip for manually typed jobID
      }

      if (jobID && /^\d{5,6}$/.test(jobID)) {
        // Existing job: get from backend
        const name = await getdesigner(jobID);
        setDesignerName(name);
      } else if (userRole === "user") {
        // New form: auto-fill from localStorage
        const name = LocalStorageService.getDesignerName() || "";
        setDesignerName(name);
      }
      // If admin + no jobID: leave blank
    }

    assignDesigner();
  }, [jobID]); // dependency array includes jobID



  // PDF state
  const [hasSeenPreview, setHasSeenPreview] = useState(false);

  // ── EFFECT: Reset PDF preview when any form field changes ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hasSeenPreview) {
      setHasSeenPreview(false);
    }
  }, [
    jobID, clientName, clienttelephone, clientemail, address, city, province, postalCode,
    projectname, isSameAsbillingAddress, billingAddress, billingCity, billingProvince,
    billingPostalCode, cuttingarea, collectionList, selectedColor, doorEnabled, doorQuantity,
    doorDecoStyle, doorSelectedSeries, doorSelectedVariant, doorgripStyle, doorgripSelectedSeries,
    doorgripSelectedSize, doorgripColor, drawerEnabled, drawerQuantity, drawerDecoStyle,
    drawerSelectedSeries, drawerSelectedVariant, drawergripStyle, drawergripSelectedSeries,
    drawergripSelectedSize, drawergripColor, rodEnabled, rodQuantity, rodStyle, rodColor,
    noTopFinish, mouldingStyle, nobottomFinish, moulding1Style, baseboardRemove, baseboardNotch,
    backing, drilling, countertopEnabled, countertopType, countertopColor, countertopCollection,
    tearout, basketEnabled, basketQuantity, baskettype, basketsize, basketliner, basket,
    hooksEnabled, hooktype, hookcolour, hookspercleatQuantity, cleatQuantity, hamperEnabled,
    hamperQuantity, hampertype, hampercode, hampercolour, hampersize, hamperdeco, hamperseries,
    hampervariant, additionalNotes,
    closetFinance.subtotal, closetFinance.total, closetFinance.deposit, closetFinance.balanceDue,
    closetFinance.depositOption, closetFinance.paymentMethod, closetFinance.chequeNumber,
    kitchenFinance.subtotal, kitchenFinance.total, kitchenFinance.deposit,
    kitchenFinance.progressPayment, kitchenFinance.balanceDue, kitchenFinance.paymentMethod, kitchenFinance.chequeNumber,
    statusCard, statusCardNumber,
    hasCondos, housenumber, validateJobIdLength, standardDrill

  ]);



  // Sub-components now imported from src/components/ — local aliases preserve all JSX references
  //const DecoSelect = DecoSelectShared;
  const DecoOptions = DecoOptionsComponent;
  const GripOptions = GripOptionsComponent;
  const SpecialDoorOptions = SpecialDoorOptionsComponent;
  const RodOptions = RodOptionsComponent;
  const CountertopOptions = CountertopOptionsComponent;

  async function getdesigner(jobID) {
    // fetch returns object -> pick just .name
    const dbDesigner = await GeneratePDFService.fetchDesigner(jobID);
    const cachedDesigner = LocalStorageService.getDesignerName();

    const designerName = dbDesigner?.name ?? cachedDesigner ?? "";

    /*console.log("Designer:", designerName);
    console.log("DB Designer:", dbDesigner);
    console.log("Cache Designer:", cachedDesigner);*/

    logger.debug("getdesigner:", { dbDesigner, cachedDesigner });

    return designerName;
  }

  // Client Details configuration (2 fields per row)
  const clientDetails = useMemo(
    () => [
      {
        key: "jobID",
        label: "Job ID",
        value: jobID || "",
        setValue: setJobID,
        type: "text",

        inputProps: {
          pattern: validateJobIdLength ? `\\d{${validateJobIdLength}}` : "\\d*",
          maxLength: validateJobIdLength || 6,
        },

        error: touched.jobID && jobID.length !== Number(validateJobIdLength),
        helperText:
          touched.jobID &&
            validateJobIdLength &&
            jobID.length !== Number(validateJobIdLength)
            ? `Enter a ${validateJobIdLength}-digit Job ID`
            : "",

        onChange: async (e) => {
          const clean = e.target.value.replace(/\D/g, "");
          setJobID(clean);

          if (clean.length === Number(validateJobIdLength)) {
            const res = await GeneratePDFService.GetJobDetailById(clean);
            if (res) notify("Job ID already exists!", "warning");
          }
        },

        disabled: !validateJobIdLength,
      },
      {
        key: "designerName",
        label: "Designer Name",
        value: designerName,
        type: "text",
        inputProps: { pattern: "[A-Za-z ]+", maxLength: 50 },
        //error: touched.designerName && (!designerName || !/^[A-Za-z ]+$/.test(designerName)),
        //helperText: touched.designerName && !designerName ? "Required" : "",
        //onChange: (e) => LocalStorageService.getDesignerName(e.target.value),
        onChange: (e) => {
          setDesignerName(e.target.value);
          LocalStorageService.setDesignerName(e.target.value);
        },

      },
      {
        key: "clientName",
        label: "Client Name",
        value: clientName || "",
        setValue: setClientName,
        type: "text",
        inputProps: { pattern: "[A-Za-z ]+", maxLength: 100 },
        error:
          touched.clientName &&
          (!clientName || !/^[A-Za-z]+ [A-Za-z]+$/.test(clientName)),
        helperText:
          touched.clientName && !clientName
            ? "Required"
            : touched.clientName && !/^[A-Za-z]+ [A-Za-z]+$/.test(clientName)
              ? "Enter First Name and Last Name (e.g., John Doe)"
              : "",
        onChange: (e) =>
          setClientName(e.target.value.replace(/[^A-Za-z ]/g, "")),
      },
      {
        key: "clienttelephone",
        label: "Client Telephone",
        value: clienttelephone || "",
        setValue: setClientTelephone,
        type: "text",
        inputProps: { pattern: "\\d{10}", maxLength: 12 }, // maxLength increased to accommodate the hyphens
        error:
          touched.clienttelephone &&
          !/^\d{3}-\d{3}-\d{4}$/.test(clienttelephone),
        helperText:
          touched.clienttelephone &&
            !/^\d{3}-\d{3}-\d{4}$/.test(clienttelephone)
            ? "Enter a 10-digit Telephone Number"
            : "",
        onChange: (e) => {
          // Remove any non-digit characters
          let rawValue = e.target.value.replace(/\D/g, "");

          // Format the number as XXX-XXX-XXXX
          if (rawValue.length <= 3) {
            rawValue = rawValue.replace(/(\d{1,3})/, "$1");
          } else if (rawValue.length <= 6) {
            rawValue = rawValue.replace(/(\d{3})(\d{1,3})/, "$1-$2");
          } else {
            rawValue = rawValue.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3");
          }
          setClientTelephone(rawValue);
        },
      },
      {
        key: "clientemail",
        label: "Client E-mail",
        value: clientemail || "",
        setValue: setClientEmail,
        type: "text",
        inputProps: { maxLength: 50 },
        error: touched.clientemail && !clientemail,
        helperText:
          touched.clientemail && !clientemail ? "Enter a Client E-mail" : "",
        onChange: (e) => setClientEmail(e.target.value),
      },
      {
        key: "closetsFormCount",
        label: "Total Closets",
        value: closetsFormCount,
        setValue: setClosetsFormCount,
        type: "text",
        inputProps: { maxLength: 2 },
        error: touched.closetsFormCount && !closetsFormCount,
        helperText:
          touched.closetsFormCount && !closetsFormCount
            ? "Enter a valid number"
            : "",
        onChange: (e) => setClosetsFormCount(e.target.value),
      },
    ],
    [
      jobID,
      designerName,
      clientName,
      clientemail,
      clienttelephone,
      closetsFormCount,
      touched.jobID,
      touched.clientName,
      touched.clienttelephone,
      touched.clientemail,
      touched.closetsFormCount,
      validateJobIdLength,
    ]
  );

  const billingAddressFields = [
    {
      key: "billingAddress",
      label: "Address",
      value: billingAddress,
      custom: true,
      billingAddress: billingAddress,
      streetName: billingStreetName,
      setBillingAddress: setBillingAddress,
      setStreetName: setBillingStreetName,
      error:
        billingAddress &&
        touched.billingAddress &&
        (!billingStreetName ||
          billingStreetName.trim().toLowerCase() ===
          billingAddress.trim().toLowerCase()),
      helperText:
        touched.billingAddress && !billingStreetName ? "Required" : "",
    },
    {
      key: "billingcity",
      label: "City",
      value: billingCity,
      type: "text",
      inputProps: { maxLength: 50 },
      error: touched.billingCity && !billingCity,
      helperText: touched.billingCity && !billingCity ? "Required" : "",
      onChange: (e) => setBillingCity(e.target.value.toUpperCase()),
    },
    {
      key: "billingprovince",
      label: "Province",
      value: billingProvince,
      type: "text",
      inputProps: { maxLength: 2 },
      error: touched.billingProvince && !billingProvince,
      helperText: touched.billingProvince && !billingProvince ? "Required" : "",
      onChange: (e) => setBillingProvince(e.target.value.toUpperCase()),
    },
    {
      key: "billingpostalCode",
      label: "Postal Code",
      value: billingPostalCode,
      type: "text",
      inputProps: { maxLength: 7 }, // maxLength to handle the space and the 6 characters
      error:
        touched.billingPostalCode &&
        !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(billingPostalCode),
      helperText:
        touched.billingPostalCode &&
          !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(billingPostalCode)
          ? "Enter a valid postal code (e.g., L4N 9A7)"
          : "",
      onChange: (e) => {
        // Remove any non-alphanumeric characters
        let rawValue = e.target.value.replace(/[^A-Za-z0-9]/g, "");

        // Apply the format: L4N 9A7 (ensure uppercase and space after the 3rd character)
        if (rawValue.length <= 3) {
          rawValue = rawValue.toUpperCase().replace(/([A-Za-z0-9]{1,3})/, "$1");
        } else if (rawValue.length <= 6) {
          rawValue = rawValue
            .toUpperCase()
            .replace(/([A-Za-z0-9]{3})([A-Za-z0-9]{1,3})/, "$1 $2");
        } else {
          rawValue = rawValue
            .toUpperCase()
            .replace(
              /([A-Za-z0-9]{3})([A-Za-z0-9]{3})([A-Za-z0-9]{1,1})/,
              "$1 $2$3"
            );
        }
        setBillingPostalCode(rawValue);
      },
    },
  ];

  // Client Address fields
  const clientAddressFields = [
    {
      key: "address",
      label: "Address",
      value: address,
      custom: true,
      address: address,
      jobsitestreetName: jobsiteStreetName,
      setAddress: setAddress,
      setJobSiteStreetName: setJobSiteStreetName,
      error:
        address &&
        touched.address &&
        (!jobsiteStreetName ||
          jobsiteStreetName.trim().toLowerCase() ===
          address.trim().toLowerCase()),
      helperText: touched.address && !jobsiteStreetName ? "Required" : "",
    },
    {
      key: "city",
      label: "City",
      value: city,
      type: "text",
      inputProps: { maxLength: 50 },
      error: touched.city && !city,
      helperText: touched.city && !city ? "Required" : "",
      onChange: (e) => setCity(e.target.value.toUpperCase()),
    },
    {
      key: "province",
      label: "Province",
      value: province,
      type: "text",
      inputProps: { maxLength: 2 },
      error: touched.province && !province,
      helperText: touched.province && !province ? "Required" : "",
      onChange: (e) => setProvince(e.target.value.toUpperCase()),
    },
    {
      key: "postalCode",
      label: "Postal Code",
      value: postalCode,
      type: "text",
      inputProps: { maxLength: 7 }, // maxLength to handle the space and the 6 characters
      error:
        touched.postalCode &&
        !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(postalCode),
      helperText:
        touched.postalCode &&
          !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(postalCode)
          ? "Enter a valid postal code (e.g., L4N 9A7)"
          : "",
      onChange: (e) => {
        // Remove any non-alphanumeric characters
        let rawValue = e.target.value.replace(/[^A-Za-z0-9]/g, "");

        // Apply the format: L4N 9A7 (ensure uppercase and space after the 3rd character)
        if (rawValue.length <= 3) {
          rawValue = rawValue.toUpperCase().replace(/([A-Za-z0-9]{1,3})/, "$1");
        } else if (rawValue.length <= 6) {
          rawValue = rawValue
            .toUpperCase()
            .replace(/([A-Za-z0-9]{3})([A-Za-z0-9]{1,3})/, "$1 $2");
        } else {
          rawValue = rawValue
            .toUpperCase()
            .replace(
              /([A-Za-z0-9]{3})([A-Za-z0-9]{3})([A-Za-z0-9]{1,1})/,
              "$1 $2$3"
            );
        }

        setPostalCode(rawValue);
      },
    },
  ];

  const projectnamefield = [
    {
      key: "projectname",
      label: "",
      value: projectname,
      type: "text",
      inputProps: { maxLength: 100 },
      error: touched.projectname && !projectname,
      helperText: touched.projectname && !projectname ? "Required" : "",
      onChange: (e) => setProjectName(e.target.value),
    },
  ];

  const designatedCuttingAreaFields = [
    {
      key: "cuttingarea",
      label: "Designated Cutting Area",
      value: cuttingarea,
      type: "select",
      error: touched.cuttingarea && !cuttingarea,
      helperText:
        touched.cuttingarea && !cuttingarea
          ? "Please select a cutting area"
          : "",
      onChange: (e) => setDesignatedCuttingArea(e.target.value),
      options: cuttingAreaOptions,
    },
  ];

  const extendedClientAddressFields = [
    // 1) All the individual address lines:
    ...clientAddressFields,
    ...designatedCuttingAreaFields,

    // 3) Is it Condo?
    {
      key: "hasCondos",
      label: "Is it Condo?",
      value:
        hasCondos === true
          ? "Yes"
          : hasCondos === false
            ? "No"
            : "Not Specified",
      onChange: (e) => setHasCondos(e.target.value),
    },
    // 4) Condo Count (always included)
    {
      key: "housenumber",
      label: "House Number",
      value: housenumber ?? "Not Provided", // fallback if housenumber is null/undefined
      onChange: (e) => sethousenumber(e.target.value),
    },
    {
      key: "floorInfo",
      label: "Floor",
      value: hasCondos === true ? (floorInfo || "N/A") : "N/A",
      onChange: (e) => setFloorInfo(e.target.value),
    },
    {
      key: "longWalk",
      label: "Long Walk",
      value: longWalk === true ? "Yes" : longWalk === false ? "No" : "Not Specified",
      onChange: (e) => setLongWalk(e.target.value),
    }

  ];


  const tearoutfields = [
    {
      key: "tearout",
      label: "Tear Out",
      value: tearout,
      type: "select",
      error: touched.tearout && !tearout,
      helperText: touched.tearout && !tearout ? "Please select a tear out" : "",
      onChange: (e) => setTearOut(e.target.value),
      options: tearOutOptions,
    },
  ];
  // Additional Information
  const additionalDetails = [
    {
      key: "additionalNotes",
      label: "Additional Notes",
      value: additionalNotes,
      type: "text",
      inputProps: { maxLength: 670 },
      helperText: `${additionalNotes.length}/670 characters`,
      error: additionalNotes.length > 670,
      onChange: (e) => {
        const text = e.target.value;

        // Only update if within limit
        if (text.length <= 670) {
          setAdditionalNotes(text);
        } else {
          notify("Character limit exceeded! Maximum 670 characters allowed for additional notes.", "warning");
        }
      }
    },
  ];

  const cabinetConstructionFields = [
    {
      key: "cabinetConstruction",
      label: "",
      value: cabinetConstruction,
      error: touched.cabinetConstruction && !cabinetConstruction.trim(),
      helperText: "Cabinet construction is required.",
      onChange: (e) => setCabinetConstruction(e.target.value),
    },
  ];

  const doorPricingFields = [
    { key: "doorStyle", label: "Door Style", value: doorStyle, error: touched.doorStyle && !doorStyle.trim(), helperText: "Door style is required.", onChange: (e) => setDoorStyle(e.target.value) },
    { key: "doorMaterial", label: "Door Material", value: doorMaterial, error: touched.doorMaterial && !doorMaterial.trim(), helperText: "Door material is required.", onChange: (e) => setDoorMaterial(e.target.value) },
    { key: "doorFinish", label: "Door Finish", value: doorFinish, error: touched.doorFinish && !doorFinish.trim(), helperText: "Door finish is required.", onChange: (e) => setDoorFinish(e.target.value) },
    { key: "finishEndsDummyDoors", label: "Finish Ends Dummy Doors", value: finishEndsDummyDoors, error: touched.finishEndsDummyDoors && !finishEndsDummyDoors.trim(), helperText: "This field is required.", onChange: (e) => setFinishEndsDummyDoors(e.target.value) },
    { key: "finishEndsFlatPanels", label: "Finish Ends Flat Panels", value: finishEndsFlatPanels, error: touched.finishEndsFlatPanels && !finishEndsFlatPanels.trim(), helperText: "This field is required.", onChange: (e) => setFinishEndsFlatPanels(e.target.value) },
    { key: "finishEndsColumns", label: "Finish Ends Columns", value: finishEndsColumns, error: touched.finishEndsColumns && !finishEndsColumns.trim(), helperText: "This field is required.", onChange: (e) => setFinishEndsColumns(e.target.value) },
    { key: "finishInteriorUppers", label: "Finish Interior Uppers", value: finishInteriorUppers, error: touched.finishInteriorUppers && !finishInteriorUppers.trim(), helperText: "This field is required.", onChange: (e) => setFinishInteriorUppers(e.target.value) },
    { key: "glassTypeDetail", label: "Glass Type", value: glassTypeDetail, error: touched.glassTypeDetail && !glassTypeDetail.trim(), helperText: "This field is required.", onChange: (e) => setGlassTypeDetail(e.target.value) },
  ];

  const drawerToUndermountFields = [
    { key: "drawerBoxesDetail", label: "Drawer Boxes", value: drawerBoxesDetail, error: touched.drawerBoxesDetail && !drawerBoxesDetail.trim(), helperText: "This field is required.", onChange: (e) => setDrawerBoxesDetail(e.target.value) },
    { key: "glidesDetail", label: "Glides", value: glidesDetail, error: touched.glidesDetail && !glidesDetail.trim(), helperText: "This field is required.", onChange: (e) => setGlidesDetail(e.target.value) },
    { key: "openingHardwareDetail", label: "Opening Hardware", value: openingHardwareDetail, error: touched.openingHardwareDetail && !openingHardwareDetail.trim(), helperText: "This field is required.", onChange: (e) => setOpeningHardwareDetail(e.target.value) },
    { key: "crownSystemDetail", label: "Crown System", value: crownSystemDetail, error: touched.crownSystemDetail && !crownSystemDetail.trim(), helperText: "This field is required.", onChange: (e) => setCrownSystemDetail(e.target.value) },
    { key: "faciaPanelDetail", label: "Facia Panel", value: faciaPanelDetail, error: touched.faciaPanelDetail && !faciaPanelDetail.trim(), helperText: "This field is required.", onChange: (e) => setFaciaPanelDetail(e.target.value) },
    { key: "decorRailDetail", label: "Décor Rail", value: decorRailDetail, error: touched.decorRailDetail && !decorRailDetail.trim(), helperText: "This field is required.", onChange: (e) => setDecorRailDetail(e.target.value) },
    { key: "lightValanceDetail", label: "Light Valance", value: lightValanceDetail, error: touched.lightValanceDetail && !lightValanceDetail.trim(), helperText: "This field is required.", onChange: (e) => setLightValanceDetail(e.target.value) },
    { key: "undermountPanelDetail", label: "Undermount Panel", value: undermountPanelDetail, error: touched.undermountPanelDetail && !undermountPanelDetail.trim(), helperText: "This field is required.", onChange: (e) => setUndermountPanelDetail(e.target.value) },
  ];

  const accessoriesPricingFields = [
    {
      key: "accessoriesDetail",
      label: "Accessories",
      value: accessoriesDetail,
      error: touched.accessoriesDetail && !accessoriesDetail.trim(),
      helperText: "Accessories details are required.",
      onChange: (e) => setAccessoriesDetail(e.target.value),
    },
  ];

  const remainingPricingFields = [
    { key: "customCanopyDetail", label: "Custom Canopy", value: customCanopyDetail, error: touched.customCanopyDetail && !customCanopyDetail.trim(), helperText: "This field is required.", onChange: (e) => setCustomCanopyDetail(e.target.value) },
    { key: "appliancesDoorPanelsDetail", label: "Appliances Door Panels", value: appliancesDoorPanelsDetail, error: touched.appliancesDoorPanelsDetail && !appliancesDoorPanelsDetail.trim(), helperText: "This field is required.", onChange: (e) => setAppliancesDoorPanelsDetail(e.target.value) },
    { key: "hingesDetail", label: "Hinges", value: hingesDetail, error: touched.hingesDetail && !hingesDetail.trim(), helperText: "This field is required.", onChange: (e) => setHingesDetail(e.target.value) },
    { key: "counterTopsDetail", label: "Counter Tops", value: counterTopsDetail, error: touched.counterTopsDetail && !counterTopsDetail.trim(), helperText: "This field is required.", onChange: (e) => setCounterTopsDetail(e.target.value) },
    { key: "electricalDetail", label: "Electrical", value: electricalDetail, error: touched.electricalDetail && !electricalDetail.trim(), helperText: "This field is required.", onChange: (e) => setElectricalDetail(e.target.value) },
    { key: "puckLightsDetail", label: "Puck Lights", value: puckLightsDetail, error: touched.puckLightsDetail && !puckLightsDetail.trim(), helperText: "This field is required.", onChange: (e) => setPuckLightsDetail(e.target.value) },
    { key: "plumbingDetail", label: "Plumbing", value: plumbingDetail, error: touched.plumbingDetail && !plumbingDetail.trim(), helperText: "This field is required.", onChange: (e) => setPlumbingDetail(e.target.value) },
    { key: "deliveryInstallationDetail", label: "Delivery and Installation", value: deliveryInstallationDetail, error: touched.deliveryInstallationDetail && !deliveryInstallationDetail.trim(), helperText: "This field is required.", onChange: (e) => setDeliveryInstallationDetail(e.target.value) },
  ];
  // Special Details — NOT mandatory, same large textarea style as Accessories
  const kitchenSpecialDetailsFields = [
    {
      key: "kitchenSpecialDetails",
      label: "",
      value: kitchenSpecialDetails,
      error: false,
      helperText: "",
      onChange: (e) => setKitchenSpecialDetails(e.target.value),
    },
  ];

  const buildFinancialSummaryFields = (block, blockKey) => [
    {
      key: `${blockKey}_subtotal`,
      label: "Subtotal",
      value: block.subtotal,
      type: "text",
      inputProps: { maxLength: 10 },
      error: touched[`${blockKey}_subtotal`] && (block.subtotal <= 0 || !block.subtotal),
      helperText:
        touched[`${blockKey}_subtotal`] && (block.subtotal <= 0 || !block.subtotal)
          ? "Enter a positive subtotal"
          : "",
      onChange: (e) => {
        const value = e.target.value;
        if (value > 0 || value === "") {
          block.setLastEdited("subtotal");
          block.setSubtotal(value);
        }
      },
    },
    {
      key: `${blockKey}_hst`,
      label: statusCard === true ? "HST (5%)" : "HST (13%)",
      value: block.hst,
      type: "text",
      inputProps: { readOnly: true },
      onChange: () => { },
    },
    {
      key: `${blockKey}_total`,
      label: "Total",
      value: block.total,
      type: "text",
      inputProps: { maxLength: 10 },
      error: touched[`${blockKey}_total`] && (block.total <= 0 || !block.total),
      helperText:
        touched[`${blockKey}_total`] && (block.total <= 0 || !block.total)
          ? "Enter a positive total amount"
          : "",
      onChange: (e) => {
        const value = e.target.value;
        if (value > 0 || value === "") {
          block.setLastEdited("total");
          block.setTotal(value);
        }
      },
    },
  ];

  const buildClosetPaymentFields = (block) => [
    {
      key: "closet_depositOption",
      label: "Deposit Percentage",
      value: block.depositOption,
      type: "select",
      error: touched.closet_depositOption && !block.depositOption,
      helperText: touched.closet_depositOption && !block.depositOption ? "Select Deposit Percentage" : "",
      onChange: (e) => block.setDepositOption(e.target.value),
      options: ["25%", "50%", "Custom"],
    },
    {
      key: "closet_deposit",
      label: "Deposit",
      value: block.deposit,
      type: "text",
      inputProps: { readOnly: block.depositOption !== "Custom", min: 0 },
      error: touched.closet_deposit && (block.deposit <= 0 || !block.deposit),
      helperText:
        touched.closet_deposit && (block.deposit <= 0 || !block.deposit) ? "Enter a positive deposit" : "",
      onChange: (e) => {
        const value = e.target.value;
        if (block.depositOption === "Custom" && (value === "" || parseFloat(value) >= 0)) {
          block.setDeposit(value);
        }
      },
    },
    {
      key: "closet_balanceDue",
      label: "Balance Due",
      value: block.balanceDue,
      type: "text",
      inputProps: { maxLength: 10, readOnly: true },
    },
    {
      key: "closet_paymentMethod",
      label: "Payment Method",
      value: block.paymentMethod,
      type: "select",
      error: touched.closet_paymentMethod && !block.paymentMethod,
      helperText: touched.closet_paymentMethod && !block.paymentMethod ? "Select Payment Method" : "",
      onChange: (e) => block.setPaymentMethod(e.target.value),
      options: ["Cash", "Cheque", "E-Transfer", "Financing", "Card"],
    },
    {
      key: "closet_chequeNumber",
      label: "Cheque Number",
      value: block.chequeNumber,
      type: "text",
      error: touched.closet_chequeNumber && !block.chequeNumber,
      helperText: touched.closet_chequeNumber && !block.chequeNumber ? "Enter Cheque Number" : "",
      onChange: (e) => block.setChequeNumber(e.target.value),
      showIf: block.paymentMethod === "Cheque",
    },
  ];

  const buildKitchenPaymentFields = (block) => [
    { key: "kitchen_deposit", label: "Deposit (50%)", value: block.deposit, type: "text", inputProps: { readOnly: true } },
    { key: "kitchen_progressPayment", label: "Upon Delivery (40%)", value: block.progressPayment, type: "text", inputProps: { readOnly: true } },
    { key: "kitchen_balanceDue", label: "At Completion (10%)", value: block.balanceDue, type: "text", inputProps: { readOnly: true } },
    {
      key: "kitchen_paymentMethod",
      label: "Payment Method",
      value: block.paymentMethod,
      type: "select",
      error: touched.kitchen_paymentMethod && !block.paymentMethod,
      helperText: touched.kitchen_paymentMethod && !block.paymentMethod ? "Select Payment Method" : "",
      onChange: (e) => block.setPaymentMethod(e.target.value),
      options: ["Cash", "Cheque", "E-Transfer", "Financing", "Card"],
    },
    {
      key: "kitchen_chequeNumber",
      label: "Cheque Number",
      value: block.chequeNumber,
      type: "text",
      error: touched.kitchen_chequeNumber && !block.chequeNumber,
      helperText: touched.kitchen_chequeNumber && !block.chequeNumber ? "Enter Cheque Number" : "",
      onChange: (e) => block.setChequeNumber(e.target.value),
      showIf: block.paymentMethod === "Cheque",
    },
  ];

  // One entry per type that's actually present on this job
  const financeBlocksToRender = [
    hasClosetForms && {
      key: "closet",
      title: "Closet",
      finance: closetFinance,
      financialFields: buildFinancialSummaryFields(closetFinance, "closet"),
      paymentFields: buildClosetPaymentFields(closetFinance),
    },
    hasKitchenForms && {
      key: "kitchen",
      title: "Kitchen",
      finance: kitchenFinance,
      financialFields: buildFinancialSummaryFields(kitchenFinance, "kitchen"),
      paymentFields: buildKitchenPaymentFields(kitchenFinance),
    },
  ].filter(Boolean);

  // Flow decides which payment fields render: kitchen → fixed 50/40/10,
  // closet → manual deposit percentage + balance due.


  const [isSection1Complete, setIsSection1Complete] = useState(false);

  let isClientDetailsComplete = Boolean(
    jobID && clientName && clienttelephone && clientemail
  );

  // Client Address validation
  const isAddressSectionComplete =
    address &&
    city &&
    province &&
    postalCode &&
    jobsiteStreetName &&
    (kitchenSpacesEnabled === true || projectname) &&
    hasCondos !== null &&
    hasCondos !== undefined &&
    longWalk !== null &&
    longWalk !== undefined &&
    (hasCondos !== true || floorInfo);


  // Section validation for Billing Address
  let isBillingAddressComplete = !isSameAsbillingAddress
    ? billingAddress &&
    billingCity &&
    billingProvince &&
    billingPostalCode &&
    billingStreetName
    : true;

  let isDesignatedCuttingAreaComplete = Boolean(cuttingarea);

  let isTearOutComplete = Boolean(tearout);

  let isSection2Complete = Boolean(collectionList && selectedColor);

  let doorgripFields = [
    doorgripStyle,
    doorgripSelectedSeries,
    doorgripSelectedSize,
    doorgripColor,
  ];

  let DoorgripFieldsFilled =
    // Case 1 : touch latch or finger pull no series,size,color needed
    //(["Touch Latch", "Finger Pull"].includes(doorgripStyle) && Boolean(doorgripStyle)) ||
    // Case 1: Supplies By Customer → only first 2
    (["Touch Latch"].includes(doorgripStyle) && doorgripFields.slice(0, 2).every(Boolean)) ||
    (doorgripSelectedSeries === "Supplied By Customer" &&
      doorgripFields.slice(0, 2).every(Boolean)) ||
    // Case 2: Handle or Knobs → all 4
    (["Handle", "Knobs"].includes(doorgripStyle) &&
      doorgripFields.every(Boolean)) ||
    // Case 3: Anything else → first 3
    (!["Handle", "Knobs"].includes(doorgripStyle) &&
      doorgripFields.slice(0, 3).every(Boolean));

  let isSection3Complete =
    doorEnabled === false ||
    (doorEnabled === true &&
      doorQuantity &&
      doorDecoStyle &&
      (doorDecoStyle === "Contemporary" ||
        (doorSelectedSeries &&
          (doorDecoStyle === "5-Piece" || doorSelectedVariant))) &&
      DoorgripFieldsFilled);

  let specialDoorFieldsFilled =
    specialDoorEnabled === false ||
    (specialDoorQuantity &&
      specialDoorType &&
      specialDoorSize &&
      specialDoorHeight &&
      specialDoorInsert &&
      specialDoorFrame &&
      specialDoorEdge);

  let drawergripFields = [
    drawergripStyle,
    drawergripSelectedSeries,
    drawergripSelectedSize,
    drawergripColor,
  ];

  let DrawergripFieldsFilled =
    // Case 1 : touch latch or finger pull no series,size,color needed
    //(["Touch Latch", "Finger Pull"].includes(doorgripStyle) && Boolean(doorgripStyle)) ||
    // Case 1: Supplies By Customer → only first 2
    (drawergripSelectedSeries === "Supplied By Customer" &&
      drawergripFields.slice(0, 2).every(Boolean)) ||
    // Case 2: Handle or Knobs → all 4
    (["Handle", "Knobs"].includes(drawergripStyle) &&
      drawergripFields.every(Boolean)) ||
    // Case 3: Anything else → first 3
    (!["Handle", "Knobs"].includes(drawergripStyle) &&
      drawergripFields.slice(0, 3).every(Boolean));

  let isSection4Complete =
    !drawerEnabled ||
    (drawerQuantity &&
      drawerDecoStyle &&
      (drawerDecoStyle === "Contemporary" ||
        (drawerSelectedSeries &&
          (drawerDecoStyle === "5-Piece" || drawerSelectedVariant))) &&
      DrawergripFieldsFilled);

  let isSection5Complete = !rodEnabled || (rodQuantity && rodStyle && rodColor);

  let isTopFinishComplete = !noTopFinish || mouldingStyle;
  let isBottomFinishComplete = !nobottomFinish || moulding1Style;
  let ismouldingComplete = isTopFinishComplete && isBottomFinishComplete;

  let isBaseboardSectionComplete = Boolean(baseboardRemove || baseboardNotch);
  let isBackingComplete = Boolean(backing || !backing);
  let isDrillingComplete = Boolean(drilling) && (drilling !== "Custom" || drillingtxt.trim() !== "");
  let isCountertopComplete =
    !countertopEnabled ||
    (countertopType && countertopColor && countertopCollection);

  let isCabinetConstructionComplete = Boolean(cabinetConstruction.trim());

  let isDoorPricingComplete = Boolean(
    doorStyle.trim() &&
    doorMaterial.trim() &&
    doorFinish.trim() &&
    finishEndsDummyDoors.trim() &&
    finishEndsFlatPanels.trim() &&
    finishEndsColumns.trim() &&
    finishInteriorUppers.trim() &&
    glassTypeDetail.trim()
  );

  let isDrawerToUndermountComplete = Boolean(
    drawerBoxesDetail.trim() &&
    glidesDetail.trim() &&
    openingHardwareDetail.trim() &&
    crownSystemDetail.trim() &&
    faciaPanelDetail.trim() &&
    decorRailDetail.trim() &&
    lightValanceDetail.trim() &&
    undermountPanelDetail.trim()
  );

  let isAccessoriesPricingComplete = Boolean(accessoriesDetail.trim());

  let isRemainingPricingComplete = Boolean(
    customCanopyDetail.trim() &&
    appliancesDoorPanelsDetail.trim() &&
    hingesDetail.trim() &&
    glassTypeDetail.trim() &&
    counterTopsDetail.trim() &&
    electricalDetail.trim() &&
    puckLightsDetail.trim() &&
    plumbingDetail.trim() &&
    deliveryInstallationDetail.trim()
  );

  let isKitchenSpacesStepComplete =
    kitchenSpacesEnabled === false ||
    (kitchenSpacesEnabled === true && isRemainingPricingComplete);

  /*const isFinancialSummaryComplete = Boolean(
    subtotal &&
    hst &&
    total &&
    deposit &&
    balanceDue &&
    !isNaN(subtotal) &&
    !isNaN(hst) &&
    !isNaN(total) &&
    !isNaN(deposit) &&
    !isNaN(balanceDue)
  );

  const isPaymentMethodComplete = () => {
    if (!paymentMethod) return false; // Payment method must be selected

    if (paymentMethod === "Cheque" && !chequeNumber) {
      return false; // If "Cheque" is selected, cheque number is required
    }

    //if (paymentMethod === "Card") {
    // If "Card" is selected, ensure all card details are filled
    // return Boolean(cardNumber && expDate && cvv && cardHolderName);
    //}

    // For other payment methods, no further validation is required
    return true;
  };*/
  const isClosetFinanceComplete = !hasClosetForms || Boolean(
    closetFinance.subtotal && closetFinance.hst && closetFinance.total &&
    closetFinance.deposit && closetFinance.balanceDue &&
    !isNaN(closetFinance.subtotal) && !isNaN(closetFinance.hst) &&
    !isNaN(closetFinance.total) && !isNaN(closetFinance.deposit) && !isNaN(closetFinance.balanceDue)
  );

  const isKitchenFinanceComplete = !hasKitchenForms || Boolean(
    kitchenFinance.subtotal && kitchenFinance.hst && kitchenFinance.total &&
    kitchenFinance.deposit && kitchenFinance.progressPayment && kitchenFinance.balanceDue &&
    !isNaN(kitchenFinance.subtotal) && !isNaN(kitchenFinance.hst) && !isNaN(kitchenFinance.total)
  );

  const isFinancialSummaryComplete = isClosetFinanceComplete && isKitchenFinanceComplete;

  const isPaymentMethodComplete = () => {
    const closetOk =
      !hasClosetForms ||
      (closetFinance.paymentMethod && !(closetFinance.paymentMethod === "Cheque" && !closetFinance.chequeNumber));
    const kitchenOk =
      !hasKitchenForms ||
      (kitchenFinance.paymentMethod && !(kitchenFinance.paymentMethod === "Cheque" && !kitchenFinance.chequeNumber));
    return closetOk && kitchenOk;
  };

  let isclosetComplete =
    isDesignatedCuttingAreaComplete &&
    isSection2Complete &&
    isSection3Complete &&
    specialDoorFieldsFilled &&
    isSection4Complete &&
    isSection5Complete &&
    ismouldingComplete &&
    isBaseboardSectionComplete &&
    isBackingComplete &&
    isDrillingComplete &&
    isCountertopComplete &&
    isTearOutComplete;

  let isKitchenFlowComplete = Boolean(
    isCabinetConstructionComplete &&
    isDoorPricingComplete &&
    isDrawerToUndermountComplete &&
    isRemainingPricingComplete
  );

  let isSpaceFlowComplete =
    kitchenSpacesEnabled === true ? isKitchenFlowComplete : isclosetComplete;
  // isFinancialSummaryComplete &&  // Check if Financial Summary section is complete
  // isPaymentMethodComplete() &&   // Check if Payment Method section is complete
  // isBaseboardSectionComplete;   // Check if Client's Existing Baseboard section is complete
  let isFormComplete =
    isSpaceFlowComplete && isFinancialSummaryComplete && isPaymentMethodComplete();

  // Handler to hide PDF preview
  // const handleHidePdf = () => setPdfBlobUrl(null);

  // PDF generation – extracted to usePDFGenerator hook
  const { handleSubmit } = usePDFGenerator({
    jobID,
    designerName,
    clientName,
    clientemail,
    closetsFormCount,
    billingProvince,
    templateId,
    pdfEventType,
    pdfURL,
    designFileURL,
    pricePresentationFileURL,
    /*paymentMethod,
    chequeNumber,
    subtotal,
    hst,
    total,
    deposit,
    progressPayment,
    balanceDue,
    depositOption,
    kitchenSpacesEnabled,*/
    statusCard,
    statusCardNumber,
    kitchenSpacesEnabled,

    // TODO(next pass): split the PDF's payment page into per-type blocks the
    // same way the UI is now split. Passing both blocks through, plus a
    // best-effort combined total, so nothing breaks until that's done.
    closetFinance,
    kitchenFinance,
    hasClosetForms,
    hasKitchenForms,
    subtotal: (Number(closetFinance.subtotal || 0) + Number(kitchenFinance.subtotal || 0)).toFixed(2),
    hst: (Number(closetFinance.hst || 0) + Number(kitchenFinance.hst || 0)).toFixed(2),
    total: (Number(closetFinance.total || 0) + Number(kitchenFinance.total || 0)).toFixed(2),
    deposit: closetFinance.deposit,
    progressPayment: kitchenFinance.progressPayment,
    balanceDue: closetFinance.balanceDue,
    depositOption: closetFinance.depositOption,
    paymentMethod: closetFinance.paymentMethod || kitchenFinance.paymentMethod,
    chequeNumber: closetFinance.chequeNumber || kitchenFinance.chequeNumber,
    clientDetails,
    billingAddressFields,
    extendedClientAddressFields,
    setTouched,
    setShowLoading,
    setHasSeenPreview,
    setHasChangesAfterSend,
    notify,
  });

  const role = LocalStorageService.getRole();

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS: Job loading & creation
  // getAllJobID, getAllDesignerName, getJobIdDetail, handleCreateForm,
  // handleOptionChange, handleCancelEvent, handleResetClosetsForm
  // ─────────────────────────────────────────────────────────────────────────────
  const getAllJobID = async () => {
    try {
      const userID = LocalStorageService.getUserId();
      const role = LocalStorageService.getRole();
      const jobids = await GeneratePDFService.GetAllJobId(userID, role);
      const data = jobids.map((item) => ({
        id: item.jobid,
        label: `${item.jobid} - ${item.clientName}`,
      }));
      setJobIds(data);
    } catch (err) {
      notify("Unable to load job IDs. Please try again.", "error");
    }
  };

  const getAllDesignerName = async () => {
    try {
      let designers = await LoginService.GetAllDesigerName();
      const names = designers.map((item) => ({
        id: item.userid,
        label: item.designername,
      }));
      setDesignerNames(names);
    } catch (err) {
      notify("Unable to load designer names. Please try again.", "error");
    }
  };
  // ── EFFECT: Initial load — fetch designer names and all job IDs ──
  useEffect(() => {
    getAllDesignerName();
    getAllJobID();
  }, []);

  const handleInputChange = (event, newValue) => {
    setJobIdValue(newValue);

  };

  const getJobIdDetail = async () => {
    if (!jobIdValue)
      return;
    try {
      const jobDetails = await GeneratePDFService.GetJobDetailById(jobIdValue);

      setVisibleSubmit("show");
      setVisibleClosetsForm("show");
      setIsSection1Complete(false);
      setLogId(jobDetails.logid);
      const { hasCloset, hasKitchen } = await computeFormTypeFlags(jobIdValue);
      setHasClosetForms(hasCloset);
      setHasKitchenForms(hasKitchen);

      const result2 = await GeneratePDFService.GetJobIds(jobIdValue);
      //const options = result2.map((id) => id.jobid);
      /* const options = result2.map((item) => ({
         id: item.jobid,
         label: `${item.jobid} ${
           item.projectName !== null ? " - " + item.projectName : ""
         }`,
       }));*/

      const options = result2
        .filter((item) => {
          const extractedJobId = String(item.jobid).split("-")[0];
          return extractedJobId === String(jobIdValue);
        })
        .map((item) => ({
          id: item.jobid,
          label: `${item.jobid} ${item.projectName !== null ? " - " + item.projectName : ""
            }`,
        }));


      setOptionJobIds(options);
      setClosetDisabled(false);
      setTemplateId(jobDetails?.templateid ?? null);

      const formatCanadianDateTime = (
        dateString,
        timeZone = "America/Toronto"
      ) => {
        if (!dateString) return null; // handle undefined/null safely

        return new Date(dateString).toLocaleString("en-CA", {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone, // default Toronto (EST), but you can pass "America/Vancouver" etc.
        });
      };

      const webhookValue = (jobDetails.webhookresponse);
      setPdfEventType(webhookValue?.event_type ?? null);
      setPdfCreated(formatCanadianDateTime(webhookValue?.data?.submitters?.[0]?.created_at));
      setPdfDeclined(formatCanadianDateTime(webhookValue?.data?.declined_at));
      setPdfCompleted(formatCanadianDateTime(webhookValue?.data?.submitters?.[0]?.completed_at));

      setPdfURL(webhookValue?.data?.documents?.[0]?.url || null);

      const detailsValue = JSON.parse(jobDetails.jsoncontent);
      setParentClosetsForm(detailsValue);
      //setting CLient Details
      setJobID(detailsValue.jobId);
      setClientName(detailsValue.clientName);
      setClientTelephone(detailsValue.clientTelephone);
      setClientEmail(detailsValue.clientEmail);
      setClosetsFormCount(detailsValue.closetsFormCount);
      setOriginalClosetsFormCount(detailsValue.closetsFormCount);

      if (role === "admin") {
        getAllDesignerName();

        const designerId = detailsValue.designerId ?? detailsValue.userId;
        setSelectedDesignerName(Number(designerId));
      } else {
        setSelectedDesignerName(Number(LocalStorageService.getDesignerName()));
      }

      setBillingAddress(detailsValue.billingAddress);
      setBillingCity(detailsValue.billingCity);
      setBillingPostalCode(detailsValue.billingPostalcode);
      setBillingProvince(detailsValue.billingProvince);

      if (detailsValue.billingAddress) {
        const lastWord =
          detailsValue.billingAddress.trim().split(/\s+/).pop() || "";
        setBillingStreetName(lastWord);
      }

      //Financial
      /*  if (detailsValue.finance) {
          setStatusCard(detailsValue.finance.statuscard);
          setStatusCardNumber(detailsValue.finance.statuscardnumber);
          setSubtotal(detailsValue.finance.subTotal);
          setHst(detailsValue.finance.hst);
          setTotal(detailsValue.finance.total);
        }
  
        //Payment details — shape depends on the flow the form was saved under:
        // closet saves depositePer (+ deposite/balanceDue), kitchen saves
        // deposite/progressPayment/balanceDue. Restore whichever is present.
        if (detailsValue.paymentDetail) {
          setDepositOption(detailsValue.paymentDetail.depositePer);
          setDeposit(detailsValue.paymentDetail.deposite);
          setProgressPayment(detailsValue.paymentDetail.progressPayment);
          setBalanceDue(detailsValue.paymentDetail.balanceDue);
          setPaymentMethod(detailsValue.paymentDetail.method);
          setChequeNumber(detailsValue.paymentDetail.chequeNumber);
          //setCardNumber(detailsValue.paymentDetail.cardnumber);
          //setExpDate(detailsValue.paymentDetail.expirydate);
          //setCvv(detailsValue.paymentDetail.cvv);
          //setCardHolderName(detailsValue.paymentDetail.cardholdername);
        }*/

      if (detailsValue.finance) {
        if (detailsValue.finance.closet || detailsValue.finance.kitchen) {
          // New split shape
          if (detailsValue.finance.closet) {
            setStatusCard(detailsValue.finance.closet.statuscard);
            setStatusCardNumber(detailsValue.finance.closet.statuscardnumber);
            closetFinance.setSubtotal(detailsValue.finance.closet.subTotal);
            closetFinance.setHst(detailsValue.finance.closet.hst);
            closetFinance.setTotal(detailsValue.finance.closet.total);
          }
          if (detailsValue.finance.kitchen) {
            setStatusCard(detailsValue.finance.kitchen.statuscard);
            setStatusCardNumber(detailsValue.finance.kitchen.statuscardnumber);
            kitchenFinance.setSubtotal(detailsValue.finance.kitchen.subTotal);
            kitchenFinance.setHst(detailsValue.finance.kitchen.hst);
            kitchenFinance.setTotal(detailsValue.finance.kitchen.total);
          }
        } else {
          // Legacy flat shape from before this change — route it to whichever
          // type the job currently has (best effort for old records).
          setStatusCard(detailsValue.finance.statuscard);
          setStatusCardNumber(detailsValue.finance.statuscardnumber);
          const legacyTarget = hasKitchen ? kitchenFinance : closetFinance;
          legacyTarget.setSubtotal(detailsValue.finance.subTotal);
          legacyTarget.setHst(detailsValue.finance.hst);
          legacyTarget.setTotal(detailsValue.finance.total);
        }
      }

      if (detailsValue.paymentDetail) {
        if (detailsValue.paymentDetail.closet || detailsValue.paymentDetail.kitchen) {
          if (detailsValue.paymentDetail.closet) {
            closetFinance.setDepositOption(detailsValue.paymentDetail.closet.depositePer);
            closetFinance.setDeposit(detailsValue.paymentDetail.closet.deposite);
            closetFinance.setBalanceDue(detailsValue.paymentDetail.closet.balanceDue);
            closetFinance.setPaymentMethod(detailsValue.paymentDetail.closet.method);
            closetFinance.setChequeNumber(detailsValue.paymentDetail.closet.chequeNumber);
          }
          if (detailsValue.paymentDetail.kitchen) {
            kitchenFinance.setDeposit(detailsValue.paymentDetail.kitchen.deposite);
            kitchenFinance.setProgressPayment(detailsValue.paymentDetail.kitchen.progressPayment);
            kitchenFinance.setBalanceDue(detailsValue.paymentDetail.kitchen.balanceDue);
            kitchenFinance.setPaymentMethod(detailsValue.paymentDetail.kitchen.method);
            kitchenFinance.setChequeNumber(detailsValue.paymentDetail.kitchen.chequeNumber);
          }
        } else {
          const legacyTarget = hasKitchen ? kitchenFinance : closetFinance;
          legacyTarget.setDepositOption(detailsValue.paymentDetail.depositePer);
          legacyTarget.setDeposit(detailsValue.paymentDetail.deposite);
          legacyTarget.setProgressPayment(detailsValue.paymentDetail.progressPayment);
          legacyTarget.setBalanceDue(detailsValue.paymentDetail.balanceDue);
          legacyTarget.setPaymentMethod(detailsValue.paymentDetail.method);
          legacyTarget.setChequeNumber(detailsValue.paymentDetail.chequeNumber);
        }
      }
      setVisibleFinancialSummary(true);
      setIsJobDetailViewOpen("show");
      setDesignFileURL(detailsValue.designSheetURL);
      setPricePresentationFileURL(detailsValue.pricePresentationURL);
    } catch (err) {
      notify("Unable to load job details. Please try again.", "error");
    }
  };

  const handleCancelEvent = () => {
    setHasSeenPreview(false);
    setLogId(0);
    //setting CLient Details
    setJobID("");
    setClientName("");
    setClientTelephone("");
    setClientEmail("");
    setClosetsFormCount("");
    setOriginalClosetsFormCount(0);   // ← ADD THIS
    setHasClosetForms(false);
    setHasKitchenForms(false);
    //billing
    setBillingAddress("");
    setBillingCity("");
    setBillingPostalCode("");
    setBillingProvince("");

    //setting address
    setAddress("");
    setCity("");
    setProvince("");
    setPostalCode("");
    setProjectName("");
    setHasCondos("");
    sethousenumber("");

    /*//Financial
    setSubtotal("");
    setHst("");
    setTotal("");
    setStatusCard("");
    setStatusCardNumber("");

    /*  //Payment details
      setDepositOption("");
      setDeposit("");
      setBalanceDue("");
      setPaymentMethod("");
      setChequeNumber("");
      //setCardNumber("");
      //setExpDate("");
      //setCvv("");
      //setCardHolderName("");*/
    //Payment details
    /*setDeposit("");
    setProgressPayment("");
    setBalanceDue("");
    setPaymentMethod("");
    setChequeNumber("");*/
    setStatusCard("");
    setStatusCardNumber("");
    closetFinance.setSubtotal("");
    closetFinance.setHst("");
    closetFinance.setTotal("");
    closetFinance.setDepositOption("");
    closetFinance.setDeposit("");
    closetFinance.setBalanceDue("");
    closetFinance.setPaymentMethod("");
    closetFinance.setChequeNumber("");
    kitchenFinance.setSubtotal("");
    kitchenFinance.setHst("");
    kitchenFinance.setTotal("");
    kitchenFinance.setDeposit("");
    kitchenFinance.setProgressPayment("");
    kitchenFinance.setBalanceDue("");
    kitchenFinance.setPaymentMethod("");
    kitchenFinance.setChequeNumber("");
    setHasClosetForms(false);
    setHasKitchenForms(false);

    setIsSection1Complete(false);

    setIsJobFilterViewOpen("none");
    setIsJobDetailViewOpen("show");

    setVisibleSubmit("show");
    setVisibleClosetsForm("none");
    setJobIdValue(null);
    handleResetClosetsForm();
    setVisibleFinancialSummary(false);
    getAllDesignerName();
    setSelectedDesignerName(LocalStorageService.getUserId());
    setPdfEventType("");
    setPdfCreated("");
    setPdfDeclined("");
    setPdfCompleted("");
    setPdfURL("");
    setDesignFileURL("");
    setPricePresentationFileURL("");
    setIsNew(true);
  };

  const handleResetClosetsForm = () => {
    setIsSection1Complete(false);
    isBillingAddressComplete = false;
    isDesignatedCuttingAreaComplete = false;
    isSection2Complete = false;
    isSection3Complete = false;
    specialDoorFieldsFilled = false;
    isSection4Complete = false;
    isSection5Complete = false;
    ismouldingComplete = false;
    isCountertopComplete = false;
    isBaseboardSectionComplete = false;



    // biiling
    setIsSameAsbillingAddress(false);
    setKitchenSpacesEnabled(undefined);
    setAddress("");
    setCity("");
    setProvince("");
    setPostalCode("");
    setProjectName("");
    setHasCondos(null);
    sethousenumber("");
    setFloorInfo("");
    setLongWalk(null);

    // Design
    setDesignatedCuttingArea("");
    setCollectionList("");
    setSelectedColor("");

    //door
    setIsSameAsCollectionColor(false);
    setClosetCollection("");
    setDoorColor("");
    setDoorEnabled();
    setDoorQuantity("");
    setDoorDecoStyle("");
    setDoorSelectedSeries("");
    setDoorSelectedVariant("");
    setDoorgripStyle("");
    setDoorgripSelectedSeries("");
    setDoorgripSelectedSize("");
    setDoorgripColor("");
    // Special Door fields
    setSpecialDoorSectionError(false);
    setSpecialDoorEnabled("");
    setSpecialDoorQuantity("");
    setSpecialDoorType("");
    setSpecialDoorSize("");
    setSpecialDoorHeight("");
    setSpecialDoorInsert("");
    setSpecialDoorFrame("");
    setSpecialDoorEdge("");
    setSpecialDoorgripStyle("");
    setSpecialDoorgripSelectedSeries("");
    setSpecialDoorgripSelectedSize("");
    setSpecialDoorgripColor("");


    //drawers
    setIsSameAsCollColor(false);
    setClosetColl("");
    setDrawerColor("");
    setIsFrontSameAsCollColor(false);
    setFrontClosetColl("");
    setDrawerFrontColor("");
    setDrawerEnabled();
    setDrawerQuantity("");
    setDrawerDecoStyle("");
    setDrawerSelectedSeries("");
    setDrawerSelectedVariant("");
    setDrawergripStyle("");
    setDrawergripSelectedSeries("");
    setDrawergripSelectedSize("");
    setDrawergripColor("");

    //rods
    setRodEnabled();
    setRodQuantity("");
    setRodColor("");
    setRodStyle("");

    //racks
    setRackLists([]);
    setRackEnabled(false);
    // setRackQuantity("");
    // setRackType("");
    // setRackCode("");
    // setRackColour("");
    // setRackSize("");

    // Baskets
    setBasketLists([]);
    setBasketEnabled(false);
    //setBasketQuantity("");
    //setBasketType("");
    // setBasketSize("");
    // setBasketLiner("");
    //setBasket("");

    // Hooks
    setHooksLists([]);
    setHooksEnabled(false);
    //setHookType("");
    //setHookColour("");
    //setHooksPerCleatQuantity("");
    //setCleatQuantity("");

    // Hampers
    setHamperLists([]);
    setHamperEnabled(false);
    //setHamperQuantity("");
    //setHamperType("");
    //setHamperCode("");
    //setHamperColour("");
    //setHamperSize("");
    //setHamperDeco("");
    //setHamperSeries("");
    //setHamperVariant("");

    //set Moulding
    setNoTopFinish();
    setMouldingStyle("");
    setNoBottomFinish();
    setMoulding1Style("");

    setBaseboardRemove(false);
    setBaseboardNotch(false);

    setBacking();
    setDrilling();
    setDrillingtxt("");
    setSpecialDoorFieldErrors({});



    //CouterTop
    setCountertopEnabled();
    setCountertopType("");
    setCountertopColor("");
    setCountertopCollection("");
    setTearOut("");
    setAdditionalNotes("");
    setStandardDrill("");
    setClosetDisabled(false);
  };

  const handleCreateForm = async () => {
    try {
      if (closetsFormCount <= 0) {
        notify("Please enter valid closets form count!", "warning");
        return;
      }
      if (closetsFormCount < originalClosetsFormCount) {
        notify("You can't decrease the closets form count!", "warning");
        setClosetsFormCount(originalClosetsFormCount);
        return;
      }

      let filters = {
        logId: logId,
        jobId: jobID,
        clientName: clientName,
        designerId:
          role === "admin"
            ? selectedDesignerName
            : LocalStorageService.getUserId(),
        userId: localStorage.getItem("userID"),
        clientTelephone: clienttelephone,
        clientEmail: clientemail,
        closetsFormCount: closetsFormCount,
        billingAddress: billingAddress,
        billingCity: billingCity,
        billingProvince: billingProvince,
        billingPostalcode: billingPostalCode,
        designSheetURL: designFileURL,
        pricePresentationURL: pricePresentationFileURL,
      };
      if (logId !== 0) {
        filters = {
          ...filters,
          finance: parentClosetsForm.finance,
          paymentDetail: parentClosetsForm.paymentDetail,
        };
      }
      const result = await GeneratePDFService.FilterToPDF(filters);
      if (result) {
        notify("Successfully saved client details!", "success");
        setParentClosetsForm(JSON.parse(result.jsoncontent));
        setVisibleSubmit("none");
        setVisibleClosetsForm("show");
        const result2 = await GeneratePDFService.GetJobIds(jobID);
        //const options = result2.map((id) => id.jobid);
        /*const options = result2.map((item) => ({
          id: item.jobid,
          label: `${item.jobid} ${
            item.projectName !== null ? " - " + item.projectName : ""
          }`,
        }));*/
        const options = result2.filter((item) => {
          // Extract the Job ID portion before the first hyphen
          const extractedJobId = String(item.jobid).split("-")[0];
          return extractedJobId === String(jobID);
          // For line 5437, use jobIdValue instead of jobID
        })
          .map((item) => ({
            id: item.jobid,
            label: `${item.jobid} ${item.projectName !== null ? " - " + item.projectName : ""
              }`,
          }));

        setOptionJobIds(options);
        setIsTotalClosetsDisabled(true);

        const name = await getdesigner(jobID); // assuming you’ve defined this helper
        setDesignerName(name);

      }
    } catch (err) {
      notify("Unable to save client details. Please try again.", "error");
    }
  };

  const handleOptionChange = async (event) => {
    setSelectedJobIdOption(event.target.value);
    setIsSection1Complete(true);
    handleGetChildJsonContent(event.target.value);
    setClosetDisabled(true);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS: Closet form save & submit
  // handleClosetsFormSubmit: validates all design fields then saves to backend
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClosetsFormSubmit = async (shouldSubmit = true, options = {}) => {
    try {
      const { skipValidation = false, skipAlerts = false } = options;
      setTouched({
        cuttingarea: true,
        address: true,
        city: true,
        province: true,
        postalCode: true,
        projectname: true,
        hasCondos: true,      // ADD THIS
        longWalk: true,
        kitchenSpacesEnabled: true,      // ADD THIS
        collectionList: true,
        selectedColor: true,
        doorEnabled: true,
        doorQuantity: true,
        closetCollection: true,
        isSameAsCollectionColor: true,
        doorColor: true,
        doorDecoStyle: true,
        doorSelectedSeries: true,
        doorSelectedVariant: true,
        doorgripStyle: true,
        doorgripSelectedSeries: true,
        doorgripSelectedSize: true,
        doorgripColor: true,
        specialDoorQuantity: true,
        specialDoorType: true,
        specialDoorHeight: true,
        specialDoorSize: true,
        specialDoorInsert: true,
        specialDoorFrame: true,
        specialDoorEdge: true,
        drawerEnabled: true,
        drawerQuantity: true,
        closetColl: true,
        isSameAsCollColor: true,
        drawerColor: true,
        frontclosetColl: true,
        isFrontSameAsCollColor: true,
        drawerFrontColor: true,
        drawerDecoStyle: true,
        drawerSelectedSeries: true,
        drawerSelectedVariant: true,
        drawergripStyle: true,
        drawergripSelectedSeries: true,
        drawergripSelectedSize: true,
        drawergripColor: true,
        rodEnabled: true,
        rodQuantity: true,
        rodStyle: true,
        rodColor: true,
        noTopFinish: true,
        mouldingStyle: true,
        nobottomFinish: true,
        moulding1Style: true,
        baseboardRemove: true,
        baseboardNotch: true,
        backing: true,
        drilling: true,
        countertopEnabled: true,
        countertopType: true,
        countertopCollection: true,
        countertopColor: true,
        standardDrill: true,
        tearout: true,
        rackEnabled: true,
        rackQuantity: true,
        racktype: true,
        rackcode: true,
        rackcolour: true,
        racksize: true,
        basketEnabled: true,
        basketQuantity: true,
        baskettype: true,
        basketsize: true,
        basketliner: true,
        basket: true,
        hooksEnabled: true,
        hooktype: true,
        hookcolour: true,
        hookspercleatQuantity: true,
        cleatQuantity: true,
        hamperEnabled: true,
        hamperQuantity: true,
        hampertype: true,
        hampercode: true,
        hampercolour: true,
        hampersize: true,
        hamperdeco: true,
        hamperseries: true,
        hampervariant: true,
        additionalNotes: true,
        drillingtxt: true,
        cabinetConstruction: true,
        doorStyle: true,
        doorMaterial: true,
        doorFinish: true,
        finishEndsDummyDoors: true,
        finishEndsFlatPanels: true,
        finishEndsColumns: true,
        finishInteriorUppers: true,
        drawerBoxesDetail: true,
        glidesDetail: true,
        openingHardwareDetail: true,
        crownSystemDetail: true,
        faciaPanelDetail: true,
        decorRailDetail: true,
        lightValanceDetail: true,
        undermountPanelDetail: true,
        accessoriesDetail: true,
        customCanopyDetail: true,
        appliancesDoorPanelsDetail: true,
        hingesDetail: true,
        glassTypeDetail: true,
        counterTopsDetail: true,
        electricalDetail: true,
        puckLightsDetail: true,
        plumbingDetail: true,
        deliveryInstallationDetail: true,
      });
      setClosetDisabled(false);

      if ((hasCondos === null || hasCondos === undefined) && isSection1Complete) {
        notify("Please select whether it is a condo.", "warning");
        return;
      }

      if (hasCondos === true && !floorInfo && isSection1Complete) {
        notify("Please enter the floor number.", "warning");
        return;
      }

      if ((longWalk === null || longWalk === undefined) && isSection1Complete) {
        notify("Please select whether long walk is required.", "warning");
        return;
      }
      if ((kitchenSpacesEnabled === undefined || kitchenSpacesEnabled === null) && isSection1Complete) {
        notify("Please select Kitchen or Closet space type.", "warning");
        return;
      }
      if (kitchenSpacesEnabled === false) {
        if (!cuttingarea && isSection1Complete) {
          notify("Please select the designated cutting area.", "warning");
          return;
        }

        if (!collectionList && isSection1Complete && isDesignatedCuttingAreaComplete) {
          notify("Please select the collection.", "warning");
          return;
        }

        if (!selectedColor && isSection1Complete && isDesignatedCuttingAreaComplete) {
          notify("Please select the collection color.", "warning");
          return;
        }

        if (doorEnabled === undefined && isSection1Complete && isDesignatedCuttingAreaComplete && isSection2Complete) {
          notify("Please select the door section.", "warning");
          return;
        }

        if (doorEnabled && !closetCollection && isSection1Complete && isDesignatedCuttingAreaComplete && isSection2Complete) {
          notify("Please select the door collection.", "warning");
          return;
        }

        if (doorEnabled && !doorColor && isSection1Complete && isDesignatedCuttingAreaComplete && isSection2Complete) {
          notify("Please select the door collection color.", "warning");
          return;
        }
        /*if(specialDoorEnabled === undefined && isSection2Complete && isSection3Complete) {
          notify("Please select the special door section.", "warning");
          return;
        }*/
        // --- Outer validation: Yes/No not selected ---
        if (isSection2Complete && isSection3Complete) {
          if (specialDoorEnabled === undefined || specialDoorEnabled === null || specialDoorEnabled === "") {
            setSpecialDoorSectionError(true);
            notify("Please select the Special Door section.", "warning");
            return;
          } else {
            setSpecialDoorSectionError(false);
          }
        }

        // --- Inner validation: Yes selected but fields empty ---
        if (specialDoorEnabled && isSection2Complete && isSection3Complete) {
          const specialDoorErrors = {};

          if (!specialDoorQuantity) specialDoorErrors.specialDoorQuantity = true;
          if (!specialDoorType) specialDoorErrors.specialDoorType = true;
          if (!specialDoorSize) specialDoorErrors.specialDoorSize = true;
          if (!specialDoorHeight) specialDoorErrors.specialDoorHeight = true;
          if (!specialDoorInsert) specialDoorErrors.specialDoorInsert = true;
          if (!specialDoorFrame) specialDoorErrors.specialDoorFrame = true;
          if (!specialDoorEdge) specialDoorErrors.specialDoorEdge = true;
          // Grip validation for TFL Doors
          if (specialDoorType === "TFL Doors") {
            if (!specialDoorgripStyle) specialDoorErrors.specialDoorgripStyle = true;

            if (specialDoorgripSelectedSeries !== "Supplied By Customer") {
              // Supplied By Customer only needs Style + Series, skip Size/Color validation
              if (!specialDoorgripSelectedSeries) specialDoorErrors.specialDoorgripSelectedSeries = true;
              if (!specialDoorgripSelectedSize) specialDoorErrors.specialDoorgripSelectedSize = true;
              if (!specialDoorgripColor && ["Handle", "Knobs"].includes(specialDoorgripStyle)) {
                specialDoorErrors.specialDoorgripColor = true;
              }
            } else {
              // Supplied By Customer — only Series needs to be set (already is)
              if (!specialDoorgripSelectedSeries) specialDoorErrors.specialDoorgripSelectedSeries = true;
            }
          }

          if (Object.keys(specialDoorErrors).length > 0) {
            setSpecialDoorFieldErrors(specialDoorErrors);
            notify("Please fill the remaining fields.", "warning");
            return;
          } else {
            setSpecialDoorFieldErrors({});
          }
        }

        if (drawerEnabled === undefined && specialDoorFieldsFilled && isSection3Complete) {
          notify("Please select the drawer section.", "warning");
          return;
        }

        if (drawerEnabled && !closetColl && specialDoorFieldsFilled && isSection3Complete) {
          notify("Please select the drawer box collection.", "warning");
          return;
        }

        if (drawerEnabled && !drawerColor && specialDoorFieldsFilled && isSection3Complete) {
          notify("Please select the drawer box collection color.", "warning");
          return;
        }

        if (drawerEnabled && !frontclosetColl && specialDoorFieldsFilled && isSection3Complete) {
          notify("Please select the drawer box front collection.", "warning");
          return;
        }

        if (drawerEnabled && !drawerFrontColor && specialDoorFieldsFilled && isSection3Complete) {
          notify("Please select the drawer box front collection color.", "warning");
          return;
        }

        if (rodEnabled === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete) {
          notify("Please select the rod section.", "warning");
          return;
        }

        if (noTopFinish === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete) {
          notify("Please select the moulding top finish option.", "warning");
          return;
        }

        if (nobottomFinish === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete) {
          notify("Please select the moulding bottom finish option.", "warning");
          return;
        }

        if (!isBaseboardSectionComplete && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete) {
          notify("Please select the client existing baseboard option.", "warning");
          return;
        }

        if (backing === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete && ismouldingComplete && isBaseboardSectionComplete) {
          notify("Please select the backing option.", "warning");
          return;
        }

        if (drilling === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete && ismouldingComplete && isBackingComplete) {
          notify("Please select the drilling option.", "warning");
          return;
        }
        if (drilling === "Custom" && drillingtxt.trim() === "" && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete && ismouldingComplete && isBackingComplete) {
          notify("Please enter drilling notes for custom drilling.", "warning");
          setDrillingNotesError(true);
          if (drillingChangeRef.current) {
            drillingChangeRef.current.focus();
          }
          return;
        }


        if (countertopEnabled === undefined && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete && ismouldingComplete && isBackingComplete && isDrillingComplete) {
          notify("Please select the countertop section.", "warning");
          return;
        }

        if (!isTearOutComplete && specialDoorFieldsFilled && isSection3Complete && isSection4Complete && isSection5Complete && ismouldingComplete && isBackingComplete && isDrillingComplete && isCountertopComplete) {
          notify("Please select the tearout option.", "warning");
          return;
        }
        // --- Add these checks BEFORE the existing field-level validations ---

        if (rackEnabled && rackLists.length === 0) {
          notify("Please add at least one rack before saving.", "warning");
          return;
        }



        if (rackLists.length >= 0 && rackEnabled) {
          const errors = {};
          let hasError = false;

          for (let i = 0; i < rackLists.length; i++) {
            const rack = rackLists[i];
            const fields = ["racktype", "rackquantity", "rackcode", "rackcolour", "racksize"];
            for (const field of fields) {
              if (!rack[field]) {
                errors[`${i}-${field}`] = true;
                hasError = true;
              }
            }
          }

          if (hasError) {
            setRackFieldErrors(errors);
            // Find the first errored tab to show the user
            const firstErrorTab = Math.min(
              ...Object.keys(errors).map((k) => parseInt(k.split("-")[0]))
            );
            setActiveRackTab(firstErrorTab);
            notify(`Please fill all fields for rack tab ${firstErrorTab + 1}`, "warning");
            return;
          } else {
            setRackFieldErrors({});
          }
        }
        if (basketEnabled && basketLists.length === 0) {
          notify("Please add at least one basket before saving.", "warning");
          return;
        }

        if (basketLists.length >= 0 && basketEnabled) {
          const errors = {};
          let hasError = false;

          for (let i = 0; i < basketLists.length; i++) {
            const basket = basketLists[i];
            const fields = ["baskettype", "basketquantity", "basketsize", "basketliner"];
            for (const field of fields) {
              if (!basket[field]) {
                errors[`${i}-${field}`] = true;
                hasError = true;
              }
            }

            // ✅ Only validate liner size if liner is NOT "No Liner"
            if (
              basket.basketliner &&
              basket.basketliner !== "No Liner" &&
              !basket.basketlinersize
            ) {
              errors[`${i}-basketlinersize`] = true;
              hasError = true;
            }
          }


          if (hasError) {
            setBasketFieldErrors(errors);
            const firstErrorTab = Math.min(
              ...Object.keys(errors).map((k) => parseInt(k.split("-")[0]))
            );
            setActiveBasketTab(firstErrorTab);
            notify(`Please fill all fields for basket tab ${firstErrorTab + 1}`, "warning");
            return;
          } else {
            setBasketFieldErrors({});
          }
        }

        if (hooksEnabled && hooksLists.length === 0) {
          notify("Please add at least one hook before saving.", "warning");
          return;
        }


        if (hooksLists.length >= 0 && hooksEnabled) {
          const errors = {};
          let hasError = false;

          for (let i = 0; i < hooksLists.length; i++) {
            const hook = hooksLists[i];
            const fields = ["hooktype", "hookcolour", "hookspercleatQuantity", "cleatQuantity"];
            for (const field of fields) {
              if (!hook[field]) {
                errors[`${i}-${field}`] = true;
                hasError = true;
              }
            }
          }

          if (hasError) {
            setHookFieldErrors(errors);
            const firstErrorTab = Math.min(
              ...Object.keys(errors).map((k) => parseInt(k.split("-")[0]))
            );
            setActiveHooksTab(firstErrorTab);
            notify(`Please fill all fields for hook tab ${firstErrorTab + 1}`, "warning");
            return;
          } else {
            setHookFieldErrors({});
          }
        }

        if (hamperEnabled && hamperLists.length === 0) {
          notify("Please add at least one hamper before saving.", "warning");
          return;
        }

        if (hamperLists.length >= 0 && hamperEnabled) {
          const errors = {};
          let hasError = false;

          for (let i = 0; i < hamperLists.length; i++) {
            const hamper = hamperLists[i];
            const fields = ["hampertype", "hamperquantity", "hampercode", "hampercolour", "hampersize"];
            for (const field of fields) {
              if (!hamper[field]) {
                errors[`${i}-${field}`] = true;
                hasError = true;
              }
            }
          }

          if (hasError) {
            setHamperFieldErrors(errors);
            const firstErrorTab = Math.min(
              ...Object.keys(errors).map((k) => parseInt(k.split("-")[0]))
            );
            setActiveHamperTab(firstErrorTab);
            notify(`Please fill all fields for hamper tab ${firstErrorTab + 1}`, "warning");
            return;
          } else {
            setHamperFieldErrors({});
          }
        }


        if (!skipValidation && !isclosetComplete) {
          notify("Please fill the remaining fields.", "warning");
          return;
        }
      }


      if (kitchenSpacesEnabled === true) {
        if (!isCabinetConstructionComplete) {
          notify("Please fill all the details in the Cabinet Construction section.", "warning");
          return;
        }

        if (isCabinetConstructionComplete && !isDoorPricingComplete) {
          notify("Please fill all the details in the Door section.", "warning");
          return;
        }



        if (isCabinetConstructionComplete && isDoorPricingComplete && !isDrawerToUndermountComplete) {
          notify("Please fill all the details in the Kitchen Drawer Boxes.", "warning");
          return;
        }

        if (isCabinetConstructionComplete && isDoorPricingComplete && isDrawerToUndermountComplete && !isAccessoriesPricingComplete) {
          notify("Please fill all the details in the Kitchen Accessories section.", "warning");
          return;
        }

        if (isCabinetConstructionComplete && isDoorPricingComplete && isDrawerToUndermountComplete && isAccessoriesPricingComplete && !isRemainingPricingComplete) {
          notify("Please fill all the details in the Other Details section.", "warning");
          return;
        }

      }
      //  if (!isSection4Complete) {
      //    alert("Please complete Drawer Fields or disable drawer.");
      //    return;
      //  }
      //  if (!isSection5Complete) {
      //    alert("Please complete Rod Fields or disable Rod.");
      //    return;
      //  }
      // Call PDF service
      const filters = {
        jobId: selectedJobIdOption,
        clientName: clientName,
        userId: localStorage.getItem("userID"),
        projectname: projectname,
        sameasabove: isSameAsbillingAddress,
        address: address,
        city: city,
        province: province,
        postalCode: postalCode,
        floorInfo: floorInfo,
        longWalk: longWalk,
        cuttingarea: cuttingarea,
        collection: collectionList,
        collectionclr: selectedColor,
        isThereBacking: backing,
        isThereDrilling: drilling,
        drillingtxt: drillingtxt,
        DrillingOptions: standardDrill,
        tearout: tearout,
        additionalNotes: additionalNotes,
      };

      filters["condo"] = {
        hasCondos: hasCondos,
        housenumber: housenumber,
      };

      filters["existingBoard"] = {
        notch: baseboardNotch,
        remove: baseboardRemove,
      };

      filters["doors"] = {
        sameascollectioncolor: isSameAsCollectionColor,
        doorclosetcollection: closetCollection,
        doorcolor: doorColor,
        doorenabled: doorEnabled,
        quantity: doorQuantity,
        decostyle: doorDecoStyle,
        series: doorSelectedSeries,
        variant: doorSelectedVariant,
        gripType: doorgripStyle,
        gripSeries: doorgripSelectedSeries,
        gripSize: doorgripSelectedSize,
        gripColor: doorgripColor,
      };

      filters["specialdoors"] = {
        specialDoorEnabled: specialDoorEnabled,
        specialDoorQuantity: specialDoorQuantity,
        specialDoorType: specialDoorType,
        specialDoorSize: specialDoorSize,
        specialDoorHeight: specialDoorHeight,
        specialDoorInsert: specialDoorInsert,
        specialDoorFrame: specialDoorFrame,
        specialDoorEdge: specialDoorEdge,
        gripType: specialDoorgripStyle,
        gripSeries: specialDoorgripSelectedSeries,
        gripSize: specialDoorgripSelectedSize,
        gripColor: specialDoorgripColor,
      };

      filters["drawers"] = {
        sameascollcolor: isSameAsCollColor,
        drawerclosetcollection: closetColl,
        drawercolor: drawerColor,
        frontsameascollcolor: isFrontSameAsCollColor,
        frontclosetcollection: frontclosetColl,
        drawerfrontcolor: drawerFrontColor,
        drawerenabled: drawerEnabled,
        quantity: drawerQuantity,
        decostyle: drawerDecoStyle,
        series: drawerSelectedSeries,
        variant: drawerSelectedVariant,
        gripType: drawergripStyle,
        gripSeries: drawergripSelectedSeries,
        gripSize: drawergripSelectedSize,
        gripColor: drawergripColor,
      };

      filters["rods"] = {
        rodenabled: rodEnabled,
        quantity: rodQuantity,
        style: rodStyle,
        color: rodColor,
      };

      filters["racks"] = rackLists;

      filters["baskets"] = basketLists;

      filters["hooks"] = hooksLists;

      filters["hampers"] = hamperLists;

      filters["counterTop"] = {
        countertopenabled: countertopEnabled,
        type: countertopType,
        color: countertopColor,
        edge: countertopCollection,
      };

      filters["moulding"] = {
        topfinish: noTopFinish,
        top: mouldingStyle,
        bottomfinish: nobottomFinish,
        bottom: moulding1Style,
      };
      filters["kitchenSpacesEnabled"] = kitchenSpacesEnabled;
      filters["kitchenPricing"] = kitchenSpacesEnabled
        ? {
          cabinetConstruction,
          doorStyle,
          doorMaterial,
          doorFinish,
          finishEndsDummyDoors,
          finishEndsFlatPanels,
          finishEndsColumns,
          finishInteriorUppers,
          drawerBoxesDetail,
          glidesDetail,
          openingHardwareDetail,
          crownSystemDetail,
          faciaPanelDetail,
          decorRailDetail,
          lightValanceDetail,
          undermountPanelDetail,
          accessoriesDetail,
          customCanopyDetail,
          appliancesDoorPanelsDetail,
          hingesDetail,
          glassTypeDetail,
          counterTopsDetail,
          electricalDetail,
          puckLightsDetail,
          plumbingDetail,
          deliveryInstallationDetail,
          kitchenSpecialDetails,
        }
        : null;

      const result = await GeneratePDFService.UpdateJobIDContent(filters);
      if (result) {
        if (!skipAlerts) notify("Successfully saved closets form!", "success");
        const checkJobContent = await GeneratePDFService.CheckJobContent(jobID);

        const result2 = await GeneratePDFService.GetJobIds(jobID);
        //const options = result2.map((id) => id.jobid);
        /*const options = result2.map((item) => ({
          id: item.jobid,
          label: `${item.jobid} ${
            item.projectName !== null ? " - " + item.projectName : ""
          }`,
        }));*/
        const options = result2
          .filter((item) => {
            // Extract the Job ID portion before the first hyphen
            const extractedJobId = String(item.jobid).split("-")[0];
            return extractedJobId === String(jobID);
            // For line 5437, use jobIdValue instead of jobID
          })
          .map((item) => ({
            id: item.jobid,
            label: `${item.jobid} ${item.projectName !== null ? " - " + item.projectName : ""
              }`,
          }));

        setOptionJobIds(options);
        const { hasCloset, hasKitchen } = await computeFormTypeFlags(jobID);
        setHasClosetForms(hasCloset);
        setHasKitchenForms(hasKitchen);

        if (checkJobContent.length > 0) {
          handleResetClosetsForm();
          setVisibleClosetsForm("show");
        } else {
          setVisibleFinancialSummary(true);
          isFormComplete = false;
        }
      }
    } catch (err) {
      notify("Unable to save closet form. Please try again.", "error");
    }
  };

  const handleGetChildJsonContent = async (childJobId) => {
    try {
      const resultArray = await GeneratePDFService.GetChildContent(childJobId);

      if (
        resultArray.length > 0 &&
        resultArray[0].jsoncontent !== null &&
        resultArray[0].jsoncontent !== ""
      ) {
        const detailsValue = JSON.parse(resultArray[0].jsoncontent);
        //setting address
        setIsSameAsbillingAddress(detailsValue.sameasabove);
        setAddress(detailsValue.address);
        setCity(detailsValue.city);
        setProvince(detailsValue.province);
        setPostalCode(detailsValue.postalCode);
        setProjectName(detailsValue.projectname);
        setHasCondos(detailsValue?.condo?.hasCondos);
        sethousenumber(detailsValue?.condo?.housenumber);
        setFloorInfo(detailsValue.floorInfo || "");
        setLongWalk(detailsValue.longWalk !== undefined ? detailsValue.longWalk : null);


        if (detailsValue.address) {
          const lastWord = detailsValue.address.trim().split(/\s+/).pop() || "";
          setJobSiteStreetName(lastWord);
        }

        // Design
        setDesignatedCuttingArea(detailsValue.cuttingarea);
        setCollectionList(detailsValue.collection);
        setSelectedColor(detailsValue.collectionclr);

        //door
        if (detailsValue.doors) {
          setIsSameAsCollectionColor(detailsValue.doors.sameascollectioncolor);
          setClosetCollection(detailsValue.doors.doorclosetcollection);
          setDoorColor(detailsValue.doors.doorcolor);
          setDoorEnabled(detailsValue.doors.doorenabled);
          setDoorQuantity(detailsValue.doors.quantity);
          setDoorDecoStyle(detailsValue.doors.decostyle);
          setDoorSelectedSeries(detailsValue.doors.series);
          setDoorSelectedVariant(detailsValue.doors.variant);
          setDoorgripStyle(detailsValue.doors.gripType);
          setDoorgripSelectedSeries(detailsValue.doors.gripSeries);
          setDoorgripSelectedSize(detailsValue.doors.gripSize);
          setDoorgripColor(detailsValue.doors.gripColor);
        }
        // Special Door fields
        if (detailsValue.specialdoors) {
          setSpecialDoorEnabled(detailsValue.specialdoors.specialDoorEnabled);
          setSpecialDoorQuantity(detailsValue.specialdoors.specialDoorQuantity);
          setSpecialDoorType(detailsValue.specialdoors.specialDoorType);
          setSpecialDoorSize(detailsValue.specialdoors.specialDoorSize);
          setSpecialDoorHeight(detailsValue.specialdoors.specialDoorHeight);
          setSpecialDoorInsert(detailsValue.specialdoors.specialDoorInsert);
          setSpecialDoorFrame(detailsValue.specialdoors.specialDoorFrame);
          setSpecialDoorEdge(detailsValue.specialdoors.specialDoorEdge);
          setSpecialDoorgripStyle(detailsValue.specialdoors.gripType || "");
          setSpecialDoorgripSelectedSeries(detailsValue.specialdoors.gripSeries || "");
          setSpecialDoorgripSelectedSize(detailsValue.specialdoors.gripSize || "");
          setSpecialDoorgripColor(detailsValue.specialdoors.gripColor || "");
        } else {
          setSpecialDoorEnabled("");
          setSpecialDoorQuantity("");
          setSpecialDoorType("");
          setSpecialDoorSize("");
          setSpecialDoorHeight("");
          setSpecialDoorInsert("");
          setSpecialDoorFrame("");
          setSpecialDoorEdge("");
          setSpecialDoorgripStyle("");
          setSpecialDoorgripSelectedSeries("");
          setSpecialDoorgripSelectedSize("");
          setSpecialDoorgripColor("");

        }

        //drawers
        if (detailsValue.drawers) {
          setIsSameAsCollColor(detailsValue.drawers.sameascollcolor);
          setClosetColl(detailsValue.drawers.drawerclosetcollection);
          setDrawerColor(detailsValue.drawers.drawercolor);
          setIsFrontSameAsCollColor(detailsValue.drawers.frontsameascollcolor);
          setFrontClosetColl(detailsValue.drawers.frontclosetcollection);
          setDrawerFrontColor(detailsValue.drawers.drawerfrontcolor);
          setDrawerEnabled(detailsValue.drawers.drawerenabled);
          setDrawerQuantity(detailsValue.drawers.quantity);
          setDrawerDecoStyle(detailsValue.drawers.decostyle);
          setDrawerSelectedSeries(detailsValue.drawers.series);
          setDrawerSelectedVariant(detailsValue.drawers.variant);
          setDrawergripStyle(detailsValue.drawers.gripType);
          setDrawergripSelectedSeries(detailsValue.drawers.gripSeries);
          setDrawergripSelectedSize(detailsValue.drawers.gripSize);
          setDrawergripColor(detailsValue.drawers.gripColor);
        }

        //rods
        if (detailsValue.rods) {
          setRodEnabled(detailsValue.rods.rodenabled);
          setRodQuantity(detailsValue.rods.quantity);
          setRodColor(detailsValue.rods.color);
          setRodStyle(detailsValue.rods.style);
        }

        if (detailsValue.racks && detailsValue.racks.length > 0) {
          setRackEnabled(true);
          // setRackQuantity(detailsValue.racks.quantity);
          // setRackType(detailsValue.racks.type);
          // setRackCode(detailsValue.racks.code);
          // setRackColour(detailsValue.racks.colour);
          // setRackSize(detailsValue.racks.size);
          setRackLists(detailsValue.racks);
        }
        else {
          setRackEnabled(false);
          setRackLists([]);
        }


        if (detailsValue.baskets && detailsValue.baskets.length > 0) {
          setBasketEnabled(true);
          // setBasketQuantity(detailsValue.baskets.quantity);
          // setBasketType(detailsValue.baskets.type);
          // setBasketSize(detailsValue.baskets.size);
          // setBasketLiner(detailsValue.baskets.liner);
          // setBasket(detailsValue.baskets.basket);
          setBasketLists(detailsValue.baskets);
        }
        else {
          setBasketEnabled(false);
          setBasketLists([]);
        }


        if (detailsValue.hooks && detailsValue.hooks.length > 0) {
          setHooksEnabled(true);
          // setHookType(detailsValue.hooks.type);
          // setHookColour(detailsValue.hooks.colour);
          // setHooksPerCleatQuantity(detailsValue.hooks.hookspercleat);
          // setCleatQuantity(detailsValue.hooks.cleatquantity);
          setHooksLists(detailsValue.hooks);
        }
        else {
          setHooksEnabled(false);
          setHooksLists([]);
        }

        if (detailsValue.hampers && detailsValue.hampers.length > 0) {
          setHamperEnabled(true);
          // setHamperQuantity(detailsValue.hampers.quantity)
          // setHamperType(detailsValue.hampers.type)
          // setHamperCode(detailsValue.hampers.code)
          // setHamperColour(detailsValue.hampers.colour)
          // setHamperSize(detailsValue.hampers.size)
          // setHamperDeco(detailsValue.hampers.hamperdeco)
          // setHamperSeries(detailsValue.hampers.hamperseries)
          // setHamperVariant(detailsValue.hampers.hampervariant)
          setHamperLists(detailsValue.hampers);
        }
        else {
          setHamperEnabled(false);
          setHamperLists([]);
        }


        //set Moulding
        if (detailsValue.moulding) {
          setNoTopFinish(detailsValue.moulding.topfinish);
          setMouldingStyle(detailsValue.moulding.top);
          setNoBottomFinish(detailsValue.moulding.bottomfinish);
          setMoulding1Style(detailsValue.moulding.bottom);
        }

        if (detailsValue.existingBoard) {
          setBaseboardNotch(detailsValue.existingBoard.notch);
          setBaseboardRemove(detailsValue.existingBoard.remove);
        }

        setBacking(detailsValue.isThereBacking);

        setDrilling(detailsValue.isThereDrilling);
        if (detailsValue.isThereDrilling === "Custom") {
          setDrillingtxt(detailsValue.drillingtxt);
        }
        if (detailsValue.isThereDrilling === "Standard") {
          setStandardDrill(detailsValue.DrillingOptions);
        }

        //CouterTop
        if (detailsValue.counterTop) {
          setCountertopEnabled(detailsValue.counterTop.countertopenabled);
          setCountertopType(detailsValue.counterTop.type);
          setCountertopColor(detailsValue.counterTop.color);
          setCountertopCollection(detailsValue.counterTop.edge);
        }
        setTearOut(detailsValue.tearout);
        setAdditionalNotes(detailsValue.additionalNotes);
        // setDesignFileURL(detailsValue.designSheetURL);
        // setPricePresentationFileURL(detailsValue.pricePresentationURL);

        setKitchenSpacesEnabled(
          detailsValue.kitchenSpacesEnabled !== undefined
            ? detailsValue.kitchenSpacesEnabled
            : undefined
        );
        if (detailsValue.kitchenPricing) {
          setCabinetConstruction(detailsValue.kitchenPricing.cabinetConstruction || "");
          setDoorStyle(detailsValue.kitchenPricing.doorStyle || "");
          setDoorMaterial(detailsValue.kitchenPricing.doorMaterial || "");
          setDoorFinish(detailsValue.kitchenPricing.doorFinish || "");
          setFinishEndsDummyDoors(detailsValue.kitchenPricing.finishEndsDummyDoors || "");
          setFinishEndsFlatPanels(detailsValue.kitchenPricing.finishEndsFlatPanels || "");
          setFinishEndsColumns(detailsValue.kitchenPricing.finishEndsColumns || "");
          setFinishInteriorUppers(detailsValue.kitchenPricing.finishInteriorUppers || "");
          setDrawerBoxesDetail(detailsValue.kitchenPricing.drawerBoxesDetail || "");
          setGlidesDetail(detailsValue.kitchenPricing.glidesDetail || "");
          setOpeningHardwareDetail(detailsValue.kitchenPricing.openingHardwareDetail || "");
          setCrownSystemDetail(detailsValue.kitchenPricing.crownSystemDetail || "");
          setFaciaPanelDetail(detailsValue.kitchenPricing.faciaPanelDetail || "");
          setDecorRailDetail(detailsValue.kitchenPricing.decorRailDetail || "");
          setLightValanceDetail(detailsValue.kitchenPricing.lightValanceDetail || "");
          setUndermountPanelDetail(detailsValue.kitchenPricing.undermountPanelDetail || "");
          setAccessoriesDetail(detailsValue.kitchenPricing.accessoriesDetail || "");
          setKitchenSpecialDetails(detailsValue.kitchenPricing.kitchenSpecialDetails || "");
          setCustomCanopyDetail(detailsValue.kitchenPricing.customCanopyDetail || "");
          setAppliancesDoorPanelsDetail(detailsValue.kitchenPricing.appliancesDoorPanelsDetail || "");
          setHingesDetail(detailsValue.kitchenPricing.hingesDetail || "");
          setGlassTypeDetail(detailsValue.kitchenPricing.glassTypeDetail || "");
          setCounterTopsDetail(detailsValue.kitchenPricing.counterTopsDetail || "");
          setElectricalDetail(detailsValue.kitchenPricing.electricalDetail || "");
          setPuckLightsDetail(detailsValue.kitchenPricing.puckLightsDetail || "");
          setPlumbingDetail(detailsValue.kitchenPricing.plumbingDetail || "");
          setDeliveryInstallationDetail(detailsValue.kitchenPricing.deliveryInstallationDetail || "");

        }
      }
    } catch (err) {
      notify("Unable to load closet form. Please try again.", "error");
    }
  };

  // ── HANDLER: Delete selected closet form ──
  const handleDeleteForm = async () => {
    try {
      if (selectedJobIdOption === "") {
        notify("Please select a closets form to delete!", "warning");
        return;
      }
      const result = await GeneratePDFService.DeleteChildJobID(
        selectedJobIdOption
      );
      if (result) {
        notify("Successfully deleted closets form!", "success");
        //let closetCount = closetsFormCount;
        //closetCount--;
        //setClosetsFormCount(closetCount);
        setClosetsFormCount((prev) => {
          const updated = prev - 1;

          setParentClosetsForm({
            ...parentClosetsForm,
            closetsFormCount: updated,
          });

          return updated;
        });

        const result2 = await GeneratePDFService.GetJobIds(jobID);
        //const options = result2.map((id) => id.jobid);
        /*const options = result2.map((item) => ({
          id: item.jobid,
          label: `${item.jobid} ${
            item.projectName !== null ? " - " + item.projectName : ""
          }`,
        }));*/
        const options = result2
          .filter((item) => {
            // Extract the Job ID portion before the first hyphen
            const extractedJobId = String(item.jobid).split("-")[0];
            return extractedJobId === String(jobID);
            // For line 5437, use jobIdValue instead of jobID
          })
          .map((item) => ({
            id: item.jobid,
            label: `${item.jobid} ${item.projectName !== null ? " - " + item.projectName : ""
              }`,
          }));

        setOptionJobIds(options);
        const { hasCloset, hasKitchen } = await computeFormTypeFlags(jobID);
        setHasClosetForms(hasCloset);
        setHasKitchenForms(hasKitchen);
        setIsTotalClosetsDisabled(true);
        setClosetDisabled(false);          // ← re-enable closets dropdown
        setSelectedJobIdOption("");         // ← clear any previous selection
        handleResetClosetsForm();           // ← clear form for fresh entry
      }
    } catch (err) {
      notify("Unable to delete closet form. Please try again.", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS: File upload (design sheet + price presentation → Azure Blob)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDesignSheetEvent = async (event) => {
    setShowLoading("block");
    let file = event.target.files[0];
    if (file && file.type !== "application/pdf") {
      notify("Please upload a PDF file.", "warning");
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
      };
      const parts = file.name.split(".");
      const extension = parts.pop();
      const now = moment();

      const fileName =
        now.format("DD-MM-YYYY") +
        "_" +
        LocalStorageService.getDesignerName() +
        "." +
        extension;
      let result = await fileUploadOnAzure(file, "design_sheet", fileName);
      if (result) {
        logger.debug("Design sheet upload result:", result);
        setDesignFileURL(result);
      } else {
        notify("Unable to upload the file!", "error");
      }
    } else {
      notify("Unable to get the design file!", "error");
    }
    setShowLoading("none");
  };

  const handlePricePresentationEvent = async (event) => {
    setShowLoading("block");
    let file = event.target.files[0];
    if (file && file.type !== "application/pdf") {
      notify("Please upload a PDF file.", "warning");
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
      };
      const parts = file.name.split(".");
      const extension = parts.pop();
      const now = moment();

      const fileName =
        now.format("DD-MM-YYYY") +
        "_" +
        LocalStorageService.getDesignerName() +
        "." +
        extension;
      let result = await fileUploadOnAzure(
        file,
        "pricepresentation_sheet",
        fileName
      );
      if (result) {
        logger.debug("Price presentation upload result:", result);
        setPricePresentationFileURL(result);
      } else {
        notify("Unable to upload the file!", "error");
      }
    } else {
      notify("Unable to get the price presentation file!", "error");
    }
    setShowLoading("none");
  };

  const fileUploadOnAzure = async (file, fileType, fileName) => {
    try {
      const urlWithToken = await AzureBlobStorageService.GenerateSASToken();
      // Create container client
      const containerClient = new ContainerClient(urlWithToken);
      const blockBlobClient = containerClient.getBlockBlobClient(
        `${jobID}/${fileType}/` + fileName
      );
      // Upload file
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
        overwrite: true,
      });
      return blockBlobClient.url;
    } catch (error) {
      logger.error("Error uploading file to Azure Blob Storage:", error);
      return "";
    }
  };



  /* console.log('Component render', {
 drilling,
 drillingtxt,
 isSection1Complete,
 isAddressSectionComplete,
 isBillingAddressComplete,
 isDesignatedCuttingAreaComplete,
 isSection2Complete,
 isSection3Complete,
 specialDoorFieldsFilled,
 isSection4Complete,
 isSection5Complete,
 isBaseboardSectionComplete
});*/

  // ─────────────────────────────────────────────────────────────────────────────
  // JSX: Main form layout
  // Sections render conditionally based on completion flags:
  //   isSection1Complete → shows address/project fields
  //   isclosetComplete   → shows accessories + financial
  //   visibleFinancialSummary → shows financial + payment cards
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      <NotificationSnackbar />
      <Loader showDisplay={showLoading}></Loader>
      <AppNavBar />
      <AppTheme>
        <Box>
          <Grid2
            container
            spacing={2}
            direction="column"
            alignItems="center"
            sx={{
              maxWidth: 600,
              margin: "0 auto",
              display: isJobFilterViewOpen,
              marginBottom: "10px"
            }}
          >
            <Grid item xs={12} width="100%">
              <Card
                //variant="outlined"
                sx={{
                  width: "95%",
                  position: "relative",
                  //borderRadius: 2,
                  //boxShadow: 3,
                  backgroundColor: "#faf9f7",
                  //border: "2px solid #352c1d",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Job ID / Create New
                  </Typography>
                  <Box>
                    {/* First Line: Autocomplete + Find Button */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Autocomplete
                        value={jobids.find((opt) => opt.id === jobIdValue) || 0}
                        options={jobids}
                        getOptionLabel={(option) =>
                          option?.label?.toString() || ""
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Job Id"
                            variant="outlined"
                          />
                        )}
                        sx={{ flex: 1 }}
                        slotProps={{
                          paper: {
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.9 rem",
                                padding: "10px 14px",
                              },
                            },
                          },
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value
                        }
                        onChange={(event, newValue) => {
                          isNewJobID.current = false;
                          setJobIdValue(newValue?.id);
                        }}
                        onInputChange={handleInputChange}
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                          height: 46,
                          width: "20%",
                          borderRadius: 2,
                          boxShadow: 2,
                          backgroundColor: "#352c1d",
                          color: "#fff",
                        }}
                        onClick={() => {
                          if (!jobIdValue) {
                            notify("Please Select a Job ID", "warning");
                            return;
                          }

                          getJobIdDetail(jobIdValue);
                        }}
                      >
                        Find
                      </Button>
                    </Box>

                    {/* Second Line: New Contract Button */}
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{
                        height: 46,
                        borderRadius: 2,
                        boxShadow: 2,
                        color: "#352c1d",
                        backgroundColor: "#faf9f7",
                        borderColor: "#352c1d",
                        fontWeight: "bold",
                      }}
                      onClick={handleCancelEvent}
                    >
                      New Contract
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid2>
        </Box>
        <form>
          <Grid2
            container
            spacing={2}
            direction="column"
            alignItems="center"
            sx={{
              maxWidth: 600,
              margin: "0 auto",
              display: isJobDetailViewOpen,
              marginBottom: "20px",
            }}
          >
            {/* Client Details Card - Always visible */}
            <Grid item xs={12} width="100%">
              <Card
                //variant="outlined"
                sx={{
                  width: "95%",
                  position: "relative",
                  //borderRadius: 2,
                  //boxShadow: 3,
                  backgroundColor: "#faf9f7",
                  //border: "2px solid #352c1d",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Client Details
                  </Typography>
                  <Grid container spacing={2}>
                    {clientDetails.map((field) => (
                      <Grid item xs={12} sm={6} md={6} key={field.key}>
                        <FormControl
                          fullWidth
                          required
                          size="small"
                          error={touched[field.key] && field.error}
                        >
                          <FormLabel>{field.label}</FormLabel>

                          {/* Admin Designer AutoComplete */}
                          {field.key === "designerName" && role === "admin" ? (
                            <Autocomplete
                              options={designerNames}
                              getOptionLabel={(option) => option.label}
                              value={
                                designerNames.find(
                                  (designer) =>
                                    designer.id === selectedDesignerName
                                ) || null
                              }
                              onChange={(event, newValue) => {
                                setSelectedDesignerName(
                                  newValue ? newValue.id : ""
                                );
                              }}
                              renderInput={(params) => (
                                <TextField {...params} variant="outlined" />
                              )}
                              fullWidth
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                            />
                          ) : (
                            <>
                              {/* JOB ID special layout */}
                              {field.key === "jobID" ? (
                                <Box
                                  display="flex"
                                  gap={1}
                                  alignItems="flex-end"
                                >
                                  {/* Dropdown only for NEW job creation */}
                                  {isNew && (
                                    <FormControl
                                      sx={{ width: 100 }}
                                      size="medium"
                                    >
                                      <Select
                                        value={validateJobIdLength}
                                        displayEmpty
                                        onChange={(e) => {
                                          setValidateJobIdLength(
                                            e.target.value
                                          );
                                          setJobID(""); // Reset on change
                                        }}
                                      >
                                        <MenuItem value="" disabled>
                                          <em>Digits</em>
                                        </MenuItem>
                                        <MenuItem value="5">5</MenuItem>
                                        <MenuItem value="6">6</MenuItem>
                                      </Select>
                                      <FormHelperText
                                        sx={{ visibility: "hidden" }}
                                      >
                                        .
                                      </FormHelperText>
                                    </FormControl>
                                  )}

                                  {/* JOB ID input */}
                                  <TextField
                                    variant="outlined"
                                    type={field.type}
                                    value={field.value}
                                    onChange={(e) =>
                                      field.onChange
                                        ? field.onChange(e)
                                        : field.setValue(e.target.value)
                                    }
                                    onBlur={() => handleBlur(field.key)}
                                    inputProps={field.inputProps}
                                    helperText={(() => {
                                      if (
                                        field.value.length > 4 &&
                                        field.value.length < 7
                                      )
                                        return " ";
                                      if (!validateJobIdLength)
                                        return "Select number of digits";
                                      if (touched[field.key] && field.error)
                                        return errorPrefix + field.helperText;
                                      if (field.value && field.value.length > 0)
                                        return " ";
                                      return " ";
                                    })()}
                                    disabled={!validateJobIdLength} // disabled until dropdown is selected
                                    sx={{
                                      // Apply grey background when disabled
                                      "& .MuiInputBase-root.Mui-disabled": {
                                        backgroundColor: "#fafafaff", // light grey fill
                                        color: "#7c7c7cff", // optional darker text to indicate disabled
                                      },
                                    }}
                                    fullWidth
                                  />
                                </Box>
                              ) : (
                                // Generic fields rendering (non-JobID)
                                <TextField
                                  variant="outlined"
                                  type={field.type}
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange
                                      ? field.onChange(e)
                                      : field.setValue(e.target.value)
                                  }
                                  onBlur={() => handleBlur(field.key)}
                                  inputProps={field.inputProps}
                                  helperText={
                                    touched[field.key] && field.error
                                      ? errorPrefix + field.helperText
                                      : ""
                                  }
                                  disabled={
                                    field.key === "closetsFormCount"
                                      ? isTotalClosetsDisabled
                                      : field.disabled
                                  }
                                  fullWidth
                                />
                              )}
                            </>
                          )}
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            {/* Billing Address Card - New Section */}
            <Grid item xs={12} width="100%">
              <Card
                //variant="outlined"
                sx={{
                  width: "95%",
                  position: "relative",
                  //borderRadius: 2,
                  //boxShadow: 3,
                  backgroundColor: "#faf9f7",
                  //border: "2px solid #352c1d",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Billing Address
                  </Typography>
                  <Grid container spacing={2}>
                    {billingAddressFields.map((field) => (
                      <Grid item xs={12} sm={6} md={6} key={field.key}>
                        <FormControl
                          fullWidth
                          required
                          size="medium"
                          error={touched[field.key] && field.error}
                        >
                          <FormLabel>{field.label}</FormLabel>

                          {field.custom ? (
                            <Box display="flex" gap={1}>
                              <FormControl sx={{ minWidth: 80 }} size="medium">
                                <Select
                                  value={field.streetName}
                                  onChange={(e) => {
                                    const newType = e.target.value || "";
                                    // Derive address-only (remove previous type suffix if present)
                                    const prevSuffix = field.streetName
                                      ? " " + field.streetName
                                      : "";
                                    const raw = field.billingAddress || "";
                                    const addressOnly = raw.endsWith(prevSuffix)
                                      ? raw.slice(
                                        0,
                                        raw.length - prevSuffix.length
                                      )
                                      : raw;
                                    // Update both pieces; store combined in billingAddress (address + suffix)
                                    field.setStreetName(newType);
                                    field.setBillingAddress(
                                      newType
                                        ? `${addressOnly} ${newType}`
                                        : addressOnly
                                    );
                                  }}
                                  displayEmpty
                                  onBlur={() => handleBlur(field.key)}
                                >
                                  <MenuItem value="" disabled>
                                    <em></em>
                                  </MenuItem>
                                  <MenuItem value="St.">St.</MenuItem>
                                  <MenuItem value="Rd">Rd</MenuItem>
                                  <MenuItem value="Lane">Lane</MenuItem>
                                  <MenuItem value="Cir">Cir</MenuItem>
                                  <MenuItem value="Ave">Ave</MenuItem>
                                  <MenuItem value="CRT">CRT</MenuItem>
                                  <MenuItem value="Dr">Dr</MenuItem>
                                  <MenuItem value="Way">Way</MenuItem>
                                  <MenuItem value="S">S</MenuItem>
                                  <MenuItem value="N">N</MenuItem>
                                  <MenuItem value="E">E</MenuItem>
                                  <MenuItem value="W">W</MenuItem>
                                  <MenuItem value="Blvd">Blvd</MenuItem>
                                  <MenuItem value="Cres.">Cres.</MenuItem>
                                  <MenuItem value="Tr.">Tr.</MenuItem>
                                </Select>
                              </FormControl>

                              <TextField
                                variant="outlined"
                                placeholder="Address"
                                // Show only the address part (hide street type in the UI)
                                value={(() => {
                                  const suffix = field.streetName
                                    ? " " + field.streetName
                                    : "";
                                  const raw = field.billingAddress || "";
                                  return raw.endsWith(suffix)
                                    ? raw.slice(0, raw.length - suffix.length)
                                    : raw;
                                })()}
                                // Store the combined value (address + streetType suffix)
                                onChange={(e) => {
                                  const addressOnly = e.target.value;
                                  const suffix = field.streetName
                                    ? " " + field.streetName
                                    : "";
                                  field.setBillingAddress(addressOnly + suffix);
                                }}
                                onBlur={() => handleBlur(field.key)}
                                size="medium"
                                fullWidth
                              />
                            </Box>
                          ) : (
                            <TextField
                              variant="outlined"
                              type={field.type}
                              value={field.value}
                              onChange={(e) =>
                                field.onChange
                                  ? field.onChange(e)
                                  : field.setValue(e.target.value)
                              }
                              onBlur={() => handleBlur(field.key)}
                              inputProps={field.inputProps}
                              helperText={
                                touched[field.key] && field.error
                                  ? errorPrefix + field.helperText
                                  : ""
                              }
                            />
                          )}

                          {touched[field.key] && field.error}
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {isClientDetailsComplete && isBillingAddressComplete && (
              <Grid2 item xs={12} width="100%">
                <CardContent
                  sx={{
                    display: visibleSubmit ? "flex" : "none",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                      borderRadius: 2,
                      boxShadow: 2,
                      backgroundColor: "#352c1d",
                      color: "#fff",
                    }}
                    onClick={handleCreateForm}
                  >
                    {logId ? "Update Job" : "Create Job"}
                  </Button>
                </CardContent>
              </Grid2>
            )}
            {isClientDetailsComplete && isBillingAddressComplete && (
              <Grid2 item xs={12} width="100%">
                <Card
                  //variant="outlined"
                  sx={{
                    width: "95%",
                    position: "relative",
                    borderRadius: 2,
                    boxShadow: "None",
                    backgroundColor: "#faf9f7",
                    //border: "1px solid  #806d56",
                  }}
                >
                  <Card
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#faf9f7",
                    }}
                  >
                    <CardContent
                      sx={{
                        display: visibleClosetsForm === "none" ? "none" : "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        flexWrap: "wrap",
                        p: 2, // padding inside the CardContent
                      }}
                    >
                      {/* SELECT DROPDOWN */}
                      <FormControl
                        sx={{
                          width: { xs: "100%", sm: 200 },
                        }}
                      >
                        <InputLabel id="closets-form-label">
                          Select Closets ID
                        </InputLabel>
                        <Select
                          labelId="closets-form-label"
                          value={selectedJobIdOption}
                          label="Selected Closets Form"
                          onChange={handleOptionChange}
                          disabled={closetDisabled}
                        >
                          {optionJobIds.map((field) => (
                            <MenuItem key={field.id} value={field.id}>
                              {field.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* BUTTONS CONTAINER */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          width: { xs: "100%", sm: "auto" },
                          justifyContent: "center",
                        }}
                      >
                        {/* DELETE BUTTON */}
                        <Button
                          variant="contained"
                          color="error"
                          sx={{
                            width: { xs: "55%", sm: 200 },
                            minHeight: "15px", // ensures it's not too tall on mobile
                            borderRadius: 2,
                            //boxShadow: 2,
                            color: "#fff",
                            p: 0.8, // control internal padding
                          }}
                          onClick={() => {
                            handleDeleteForm();
                            handleClosetsFormSubmit(true, {
                              skipValidation: true,
                              skipAlerts: true
                            },
                              !!jobIdValue
                            );
                          }}
                        >
                          Delete Form
                        </Button>

                        {/* CLEAR BUTTON */}
                        <Button
                          variant="contained"
                          sx={{
                            width: { xs: "55%", sm: 200 },
                            minHeight: 20,
                            borderRadius: 2,
                            boxShadow: 2,
                            backgroundColor: "#352c1d",
                            color: "#fff",
                            p: 0.8,
                          }}
                          onClick={() => {
                            handleResetClosetsForm();
                            handleClosetsFormSubmit(
                              true,
                              { skipValidation: true, skipAlerts: true },
                              !!jobIdValue
                            );
                          }}
                        >
                          Clear Form
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Card>
              </Grid2>
            )}
            {/* Kitchen Spaces */}
            {isSection1Complete && (
              <Grid2 item xs={12} width="100%">
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Kitchen Spaces
                    </Typography>
                    <Box
                      sx={{
                        border:
                          touched.kitchenSpacesEnabled &&
                            (kitchenSpacesEnabled === undefined || kitchenSpacesEnabled === null)
                            ? "2px solid #d32f2f"
                            : "2px solid transparent",
                        borderRadius: 1,
                        p: 1,
                        backgroundColor:
                          touched.kitchenSpacesEnabled &&
                            (kitchenSpacesEnabled === undefined || kitchenSpacesEnabled === null)
                            ? "rgba(211, 47, 47, 0.08)"
                            : "transparent",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <FormControl>
                            <RadioGroup
                              row
                              value={
                                kitchenSpacesEnabled === true
                                  ? "Kitchen"
                                  : kitchenSpacesEnabled === false
                                    ? "Closet"
                                    : ""
                              }
                              onChange={(e) => {
                                setKitchenSpacesEnabled(e.target.value === "Kitchen");
                                handleBlur("kitchenSpacesEnabled");
                              }}
                            >
                              <FormControlLabel value="Kitchen" control={<Radio />} label="Kitchen" />
                              <FormControlLabel value="Closet" control={<Radio />} label="Closet" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                    {touched.kitchenSpacesEnabled &&
                      (kitchenSpacesEnabled === undefined || kitchenSpacesEnabled === null) && (
                        <Typography variant="caption" color="error">
                          Please select Kitchen or Closet
                        </Typography>
                      )}
                  </CardContent>
                </StyledCard>
              </Grid2>
            )}
            {/* Client Address Card - New Section */}
            {isSection1Complete && (
              <Grid2 item xs={12} width="100%">
                <Card
                  //variant="outlined"
                  sx={{
                    width: "95%",
                    position: "relative",
                    borderRadius: 2,
                    //boxShadow: 3,
                    backgroundColor: "#faf9f7",
                    //border: "2px solid #352c1d",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Job Site Address
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSameAsbillingAddress}
                          onChange={(e) => {
                            setIsSameAsbillingAddress(e.target.checked);
                            // If the checkbox is selected, auto-fill the billing address
                            if (e.target.checked) {
                              setJobSiteStreetName(billingStreetName);
                              setAddress(billingAddress);
                              setCity(billingCity);
                              setProvince(billingProvince);
                              setPostalCode(billingPostalCode);
                            } else {
                              // if you want to clear job-site address when unchecking:
                              setJobSiteStreetName("");
                              setAddress("");
                              setCity("");
                              setProvince("");
                              setPostalCode("");
                              setFloorInfo("");
                              setLongWalk(null);

                            }
                          }}
                        />
                      }
                      label="Same as Billing Address"
                    />
                    <Grid container spacing={2}>
                      {clientAddressFields.map((field) => (
                        <Grid item xs={12} sm={6} md={6} key={field.key}>
                          <FormControl
                            fullWidth
                            required
                            size="medium"
                            error={touched[field.key] && field.error}
                          >
                            <FormLabel>{field.label}</FormLabel>

                            {field.custom ? (
                              <Box display="flex" gap={1}>
                                <FormControl
                                  sx={{ minWidth: 80 }}
                                  size="medium"
                                >
                                  <Select
                                    value={field.jobsitestreetName}
                                    onChange={(e) => {
                                      const newType = e.target.value || "";
                                      const prevSuffix = field.jobsitestreetName
                                        ? " " + field.jobsitestreetName
                                        : "";
                                      const raw = field.address || "";
                                      const addressOnly = raw.endsWith(
                                        prevSuffix
                                      )
                                        ? raw.slice(
                                          0,
                                          raw.length - prevSuffix.length
                                        )
                                        : raw;

                                      field.setJobSiteStreetName(newType);
                                      field.setAddress(
                                        newType
                                          ? `${addressOnly} ${newType}`
                                          : addressOnly
                                      );
                                    }}
                                    displayEmpty
                                    onBlur={() => handleBlur(field.key)}
                                  >
                                    <MenuItem value="">
                                      <em></em>
                                    </MenuItem>
                                    <MenuItem value="St.">St.</MenuItem>
                                    <MenuItem value="Rd">Rd</MenuItem>
                                    <MenuItem value="Lane">Lane</MenuItem>
                                    <MenuItem value="Cir">Cir</MenuItem>
                                    <MenuItem value="Ave">Ave</MenuItem>
                                    <MenuItem value="CRT">CRT</MenuItem>
                                    <MenuItem value="Dr">Dr</MenuItem>
                                    <MenuItem value="Way">Way</MenuItem>
                                    <MenuItem value="S">S</MenuItem>
                                    <MenuItem value="N">N</MenuItem>
                                    <MenuItem value="E">E</MenuItem>
                                    <MenuItem value="W">W</MenuItem>
                                    <MenuItem value="Blvd">Blvd</MenuItem>
                                    <MenuItem value="Cres.">Cres.</MenuItem>
                                    <MenuItem value="Tr.">Tr.</MenuItem>
                                  </Select>
                                </FormControl>

                                <TextField
                                  variant="outlined"
                                  placeholder="Address"
                                  value={(() => {
                                    const suffix = field.jobsitestreetName
                                      ? " " + field.jobsitestreetName
                                      : "";
                                    const raw = field.address || "";
                                    return raw.endsWith(suffix)
                                      ? raw.slice(0, raw.length - suffix.length)
                                      : raw;
                                  })()}
                                  onChange={(e) => {
                                    const addressOnly = e.target.value;
                                    const suffix = field.jobsitestreetName
                                      ? " " + field.jobsitestreetName
                                      : "";
                                    field.setAddress(addressOnly + suffix);
                                  }}
                                  onBlur={() => handleBlur(field.key)}
                                  size="medium"
                                  fullWidth
                                />
                              </Box>
                            ) : (
                              <TextField
                                variant="outlined"
                                type={field.type}
                                value={field.value}
                                onChange={(e) =>
                                  field.onChange
                                    ? field.onChange(e)
                                    : field.setValue(e.target.value)
                                }
                                onBlur={() => handleBlur(field.key)}
                                inputProps={field.inputProps}
                                helperText={
                                  touched[field.key] && field.error
                                    ? errorPrefix + field.helperText
                                    : ""
                                }
                              />
                            )}
                            {touched[field.key] && field.error}
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 2 }}>

                      {/* ---- Left half: Is it Condo + Floor (like Address) ---- */}
                      <Grid item xs={12} sm={6} md={6}>
                        <Box>
                          <Box display="flex" alignItems="flex-start" gap={2}>
                            {/* Condo radio */}
                            <Box sx={{ minWidth: "fit-content" }}>
                              <YesNoRadio
                                label="Is it Condo? *"
                                value={hasCondos}
                                onChange={(val) => {
                                  setHasCondos(val);
                                  handleBlur("hasCondos");
                                  if (val === false) {
                                    setFloorInfo("");
                                  }
                                }}
                              />
                              {touched.hasCondos && (hasCondos === null || hasCondos === undefined) && (
                                <Typography variant="caption" color="error">
                                  Please select whether it is a condo
                                </Typography>
                              )}
                            </Box>

                            {/* Floor — small field, only when condo = yes */}
                            {hasCondos === true && (
                              <FormControl required size="small" error={touched.floorInfo && !floorInfo} fullWidth>
                                <FormLabel>Floor</FormLabel>
                                <TextField
                                  variant="outlined"
                                  type="text"
                                  value={floorInfo}
                                  onChange={(e) => setFloorInfo(e.target.value)}
                                  onBlur={() => handleBlur("floorInfo")}
                                  placeholder="e.g., 3"
                                  error={touched.floorInfo && !floorInfo}
                                  helperText={touched.floorInfo && !floorInfo ? "Required" : ""}
                                  inputProps={{ maxLength: 3 }}
                                  size="medium"
                                />
                              </FormControl>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* ---- Right half: House Number (like City) ---- */}
                      <Grid item xs={12} sm={6} md={6}>
                        <FormControl fullWidth size="small">
                          <FormLabel>House Number</FormLabel>
                          <TextField
                            variant="outlined"
                            type="text"
                            value={housenumber}
                            onChange={(e) => sethousenumber(e.target.value)}
                            placeholder="Enter a number"
                          />
                        </FormControl>
                      </Grid>

                      {/* ---- Long Walk on next row ---- */}
                      <Grid item xs={12}>
                        <YesNoRadio
                          label="Long Walk Required? 
                          (difficult access — long driveway, trail, etc.) *"
                          value={longWalk}
                          onChange={(val) => setLongWalk(val)}
                        />
                      </Grid>

                    </Grid>
                  </CardContent>
                </Card>
              </Grid2>
            )}
            {/* Kitchen Pricing – Cabinet Construction */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Cabinet Construction
                      </Typography>
                      <Grid container spacing={2}>
                        {cabinetConstructionFields.map((field) => (
                          <Grid item xs={12} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Kitchen Pricing – Door */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true &&
              isCabinetConstructionComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kitchen Doors Details
                      </Typography>
                      <Grid container spacing={2}>
                        {doorPricingFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Kitchen Pricing – Drawer Boxes through Undermount Panel */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true &&
              isCabinetConstructionComplete &&
              isDoorPricingComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kitchen Drawers Details
                      </Typography>
                      <Grid container spacing={2}>
                        {drawerToUndermountFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Kitchen Pricing – Accessories */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true &&
              isCabinetConstructionComplete &&
              isDoorPricingComplete &&
              isDrawerToUndermountComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Accessories
                      </Typography>
                      <Grid container spacing={2}>
                        {accessoriesPricingFields.map((field) => (
                          <Grid item xs={12} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} multiline rows={4} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Kitchen Pricing – Other Details (Custom Canopy, Appliances Door Panels, Hinges,
      Glass Type, Counter Tops, Electrical, Puck Lights, Plumbing, Delivery and Installation) */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true &&
              isCabinetConstructionComplete &&
              isDoorPricingComplete &&
              isDrawerToUndermountComplete &&
              isAccessoriesPricingComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Other Details
                      </Typography>
                      <Grid container spacing={2}>
                        {remainingPricingFields.map((field) => (
                          <Grid item xs={12} sm={6} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} />
                          </Grid>
                        ))}

                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Kitchen Pricing – Special Details (optional) */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              kitchenSpacesEnabled === true &&
              isCabinetConstructionComplete &&
              isDoorPricingComplete &&
              isDrawerToUndermountComplete &&
              isAccessoriesPricingComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Special Details
                      </Typography>
                      <Grid container spacing={2}>
                        {kitchenSpecialDetailsFields.map((field) => (
                          <Grid item xs={12} key={field.key}>
                            <PricingTextField field={field} touched={touched} onBlur={handleBlur} multiline rows={4} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}



            {/* Project Name */}
            {isSection1Complete && kitchenSpacesEnabled === false && (
              <Grid item xs={12} width="100%">
                <Card
                  //variant="outlined"
                  sx={{
                    width: "95%",
                    position: "relative",
                    borderRadius: 2,
                    //boxShadow: none,
                    backgroundColor: "#faf9f7",
                    //border: "2px solid #352c1d",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Project Name
                    </Typography>
                    <Grid container spacing={2}>
                      {projectnamefield.map((field) => (
                        <Grid item xs={12} sm={6} md={6} key={field.key}>
                          <FormControl
                            fullWidth
                            //required
                            size="small"
                            error={field.error}
                          >
                            <FormLabel>{field.label}</FormLabel>
                            <TextField
                              variant="outlined"
                              type={field.type}
                              value={field.value}
                              onChange={field.onChange}
                              inputProps={field.inputProps}
                              helperText={field.helperText}
                              error={field.error}
                            />
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}


            {/* Designated Cutting Area Section */}
            {isSection1Complete && kitchenSpacesEnabled === false && (
              <Grid2 item xs={12} width="100%">
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Designated Cutting Area
                    </Typography>
                    <Grid container spacing={2}>
                      {designatedCuttingAreaFields.map((field) => (
                        <Grid item xs={12} sm={6} md={6} key={field.key}>
                          <FormControl
                            fullWidth
                            required
                            size="small"
                            error={touched[field.key] && field.error}
                          >
                            <FormLabel>{field.label}</FormLabel>
                            <Select
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={() => handleBlur(field.key)}
                              variant="outlined"
                              sx={{ height: 54 }}
                            >
                              {field.options.map((option, index) => (
                                <MenuItem key={index} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                            {touched[field.key] && field.error && (
                              <Typography variant="caption" color="error">
                                ⚠️ {field.helperText}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid2>
            )}

            {/* Collection Fields Card - Visible if Client Details complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Collection
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={6}>
                          <TextField
                            select
                            label="Collection"
                            fullWidth
                            value={collectionList}
                            onChange={(e) => setCollectionList(e.target.value)}
                            error={!collectionList}
                          >
                            {collectionOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                          <Autocomplete
                            options={colorOptions[collectionList] || []}
                            value={
                              (colorOptions[collectionList] || []).includes(
                                selectedColor
                              )
                                ? selectedColor
                                : null
                            }
                            onChange={(_e, newValue) =>
                              setSelectedColor(newValue || "")
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="Collection Color" error={!selectedColor} />
                            )}
                            fullWidth
                            sx={{ mt: 0, mb: 0 }}
                            error={!selectedColor}
                          // optional enhancements:
                          // autoHighlight
                          // clearOnEscape
                          // disablePortal
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Door Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isSection2Complete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Doors
                      </Typography>

                      {/* YES/NO */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <YesNoRadio
                            value={doorEnabled}
                            onChange={(val) => setDoorEnabled(val)}
                          />
                        </Grid>
                      </Grid>

                      {doorEnabled && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          {/* Door Quantity */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Door Quantity"
                              value={doorQuantity}

                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[1-9]\d*$/.test(value)) {
                                  setDoorQuantity(value);
                                }
                              }}
                              error={doorQuantity === 0 || doorQuantity === ""}
                            />
                          </Grid>

                          {/* Door Deco Style (Autocomplete) */}
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              options={decoStyleMenuItems}
                              value={doorDecoStyle || null}
                              onChange={(_, v) => setDoorDecoStyle(v || "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Door Deco Style"
                                  fullWidth
                                  helperText={
                                    doorDecoStyle === "Thermofoil" ? (
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                      >
                                        <WarningAmberIcon fontSize="small" />
                                        Choose Deco. (Thermofoil will be
                                        deprecated by 1st Jan 2026.)
                                      </Box>
                                    ) : (
                                      ""
                                    )
                                  }
                                  error={!doorDecoStyle}
                                />
                              )}
                            />
                          </Grid>

                          {/* SERIES + VARIANT (always 2 columns) */}
                          <DecoOptions
                            decoStyle={doorDecoStyle}
                            selectedSeries={doorSelectedSeries}
                            setSelectedSeries={setDoorSelectedSeries}
                            selectedVariant={doorSelectedVariant}
                            setSelectedVariant={setDoorSelectedVariant}
                            columns={2}
                          />

                          {/* SAME AS COLLECTION CHECKBOX (full width) */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSameAsCollectionColor}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsSameAsCollectionColor(checked);
                                    if (checked) {
                                      setDoorColor(selectedColor);
                                      setClosetCollection(collectionList);
                                    } else {
                                      setDoorColor("");
                                      setClosetCollection("");
                                    }
                                  }}
                                />
                              }
                              label="Same as Closet Selection"
                            />
                          </Grid>

                          {/* Collection (Select) — left column */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Collection"
                              fullWidth
                              value={closetCollection}
                              onChange={(e) =>
                                setClosetCollection(e.target.value)
                              }
                              disabled={isSameAsCollectionColor}
                              error={!closetCollection}
                            >
                              {collectionOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </TextField>
                          </Grid>

                          {/* Collection Color (Autocomplete) — right column */}
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              disabled={isSameAsCollectionColor}
                              options={colorOptions[closetCollection] || []}
                              value={doorColor || null}
                              onChange={(_, v) => setDoorColor(v || "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Collection Color"
                                  fullWidth
                                  error={!doorColor}
                                />
                              )}
                            />
                          </Grid>

                          {/* GRIP TITLE (full width) */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                              Grip
                            </Typography>
                          </Grid>

                          {/* Grip Type (autocomplete) */}
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              options={gripoptions}
                              value={doorgripStyle || null}
                              onChange={(_, v) => setDoorgripStyle(v || "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Grip Type"
                                  fullWidth
                                  error={!doorgripStyle}
                                />
                              )}
                            />
                          </Grid>

                          {/* Grip Options (also 2×N) */}
                          <GripOptions
                            gripStyle={doorgripStyle}
                            selectedSeries={doorgripSelectedSeries}
                            setSelectedSeries={setDoorgripSelectedSeries}
                            selectedSize={doorgripSelectedSize}
                            setSelectedSize={setDoorgripSelectedSize}
                            selectedColor={doorgripColor}
                            setSelectedColor={setDoorgripColor}
                            columns={2}
                          />
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Special Door Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isSection2Complete &&
              isSection3Complete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Special Doors
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>

                          <YesNoRadio
                            value={specialDoorEnabled}
                            onChange={(val) => {
                              setSpecialDoorEnabled(val);
                              setSpecialDoorSectionError(false);
                            }}
                          />

                        </Grid>
                      </Grid>
                      {specialDoorEnabled && (
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Special Door Quantity"
                              type="text"
                              value={specialDoorQuantity}
                              /*onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[1-9]\d*$/.test(value)) {
                                  setSpecialDoorQuantity(e.target.value);
                                }
                              }}*/
                              variant="outlined"
                              error={specialDoorQuantity === "" || specialDoorFieldErrors?.specialDoorQuantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[1-9]\d*$/.test(value)) {
                                  setSpecialDoorQuantity(value);
                                  setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorQuantity: false }));
                                }
                              }}

                            />
                          </Grid>
                          <SpecialDoorOptions
                            specialDoorType={specialDoorType}
                            setSpecialDoorType={setSpecialDoorType}
                            specialDoorSize={specialDoorSize}
                            setSpecialDoorSize={setSpecialDoorSize}
                            specialDoorHeight={specialDoorHeight}
                            setSpecialDoorHeight={setSpecialDoorHeight}
                            specialDoorInsert={specialDoorInsert}
                            setSpecialDoorInsert={setSpecialDoorInsert}
                            specialDoorFrame={specialDoorFrame}
                            setSpecialDoorFrame={setSpecialDoorFrame}
                            specialDoorEdge={specialDoorEdge}
                            setSpecialDoorEdge={setSpecialDoorEdge}
                            specialDoorFieldErrors={specialDoorFieldErrors}        // ← ADD
                            setSpecialDoorFieldErrors={setSpecialDoorFieldErrors}  // ← ADD
                            // ---- NEW GRIP PROPS ----
                            specialDoorgripStyle={specialDoorgripStyle}
                            setSpecialDoorgripStyle={setSpecialDoorgripStyle}
                            specialDoorgripSelectedSeries={specialDoorgripSelectedSeries}
                            setSpecialDoorgripSelectedSeries={setSpecialDoorgripSelectedSeries}
                            specialDoorgripSelectedSize={specialDoorgripSelectedSize}
                            setSpecialDoorgripSelectedSize={setSpecialDoorgripSelectedSize}
                            specialDoorgripColor={specialDoorgripColor}
                            setSpecialDoorgripColor={setSpecialDoorgripColor}
                          />
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Drawer Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Drawers
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <YesNoRadio
                            value={drawerEnabled}
                            onChange={(val) => setDrawerEnabled(val)}
                          />
                        </Grid>
                      </Grid>
                      {drawerEnabled && (
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Drawer Quantity"
                              type="text"
                              value={drawerQuantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[1-9]\d*$/.test(value)) {
                                  setDrawerQuantity(e.target.value);
                                }
                              }}
                              variant="outlined"
                              error={drawerQuantity === 0 || drawerQuantity === ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6}>
                            <Autocomplete
                              options={decoStyleMenuItems}
                              value={drawerDecoStyle || null}
                              onChange={(_, v) => setDrawerDecoStyle(v || "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Drawer Deco Style"
                                  fullWidth
                                  error={!drawerDecoStyle}
                                />
                              )}
                            />
                            {/* <DecoSelect
                              labelId="drawer-deco-label"
                              value={drawerDecoStyle}
                              onChange={(e) =>
                                setDrawerDecoStyle(e.target.value)
                              }
                              menuItems={decoStyleMenuItems}
                              label="Drawer Deco Style"
                            />*/}
                          </Grid>
                          <DecoOptions
                            decoStyle={drawerDecoStyle}
                            selectedSeries={drawerSelectedSeries}
                            setSelectedSeries={setDrawerSelectedSeries}
                            selectedVariant={drawerSelectedVariant}
                            setSelectedVariant={setDrawerSelectedVariant}
                            columns={2}
                          />
                          {/*<Box sx={{ mt: 2 }}>*/}
                          {/* ===== Drawer BOX SECTION ===== */}
                          <Grid container spacing={2}>
                            {/* SAME AS CLOSET CHECKBOX */}
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSameAsCollColor}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setIsSameAsCollColor(checked);

                                      if (checked) {
                                        setDrawerColor(selectedColor);
                                        setClosetColl(collectionList);
                                      } else {
                                        setDrawerColor("");
                                        setClosetColl("");
                                      }
                                    }}
                                  />
                                }
                                label="Drawer Box Same as Closet Selection"
                              />
                            </Grid>

                            {/* Drawer Box Collection (SELECT — stays SELECT) */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                select
                                fullWidth
                                label="Drawer Box Collection"
                                value={closetColl}
                                onChange={(e) => setClosetColl(e.target.value)}
                                disabled={isSameAsCollColor}
                                error={!closetColl}
                              >
                                {collectionOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </TextField>
                            </Grid>

                            {/* Drawer Box Color (AUTOCOMPLETE) */}
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                disabled={isSameAsCollColor}
                                options={colorOptions[closetColl] || []}
                                value={drawerColor || null}
                                onChange={(_, v) => setDrawerColor(v || "")}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Drawer Box Color"
                                    fullWidth
                                    error={!drawerColor}
                                  />
                                )}
                              />
                            </Grid>

                            {/* ===== Drawer FRONT SECTION ===== */}

                            {/* SAME AS CLOSET CHECKBOX */}
                            <Grid item xs={12} sx={{ mt: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isFrontSameAsCollColor}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setIsFrontSameAsCollColor(checked);

                                      if (checked) {
                                        setDrawerFrontColor(selectedColor);
                                        setFrontClosetColl(collectionList);
                                      } else {
                                        setDrawerFrontColor("");
                                        setFrontClosetColl("");
                                      }
                                    }}
                                  />
                                }
                                label="Drawer Front Same as Closet Selection"
                              />
                            </Grid>

                            {/* Drawer Front Collection (SELECT — stays SELECT) */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                select
                                fullWidth
                                label="Drawer Front Collection"
                                value={frontclosetColl}
                                onChange={(e) =>
                                  setFrontClosetColl(e.target.value)
                                }
                                disabled={isFrontSameAsCollColor}
                                error={!frontclosetColl}
                              >
                                {collectionOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </TextField>
                            </Grid>

                            {/* Drawer Front Collection Colour (AUTOCOMPLETE) */}
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                disabled={isFrontSameAsCollColor}
                                options={colorOptions[frontclosetColl] || []}
                                value={drawerFrontColor || null}
                                onChange={(_, v) =>
                                  setDrawerFrontColor(v || "")
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Drawer Front Color"
                                    fullWidth
                                    error={!drawerFrontColor}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          {/*</Box>*/}

                          {/* Grip Section - Added inside drawerEnabled */}
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Grip
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              options={gripoptions}
                              value={drawergripStyle || null}
                              onChange={(_, v) => setDrawergripStyle(v || "")}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Grip Type"
                                  fullWidth
                                  error={!drawergripStyle}
                                />
                              )}
                            />
                          </Grid>
                          <GripOptions
                            gripStyle={drawergripStyle}
                            selectedSeries={drawergripSelectedSeries}
                            setSelectedSeries={setDrawergripSelectedSeries}
                            selectedSize={drawergripSelectedSize}
                            setSelectedSize={setDrawergripSelectedSize}
                            selectedColor={drawergripColor}
                            setSelectedColor={setDrawergripColor}
                            columns={2}
                          />
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Rod Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Rods
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <YesNoRadio
                            value={rodEnabled}
                            onChange={(val) => setRodEnabled(val)}
                          />
                        </Grid>
                      </Grid>
                      {rodEnabled && (
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Rod Quantity"
                              type="text"
                              value={rodQuantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^[1-9]\d*$/.test(value)) {
                                  setRodQuantity(e.target.value);
                                }
                              }}
                              variant="outlined"
                              error={rodQuantity === 0 || rodQuantity === ""}
                            />
                          </Grid>
                          <RodOptions
                            rodStyle={rodStyle}
                            setRodStyle={setRodStyle}
                            rodColor={rodColor}
                            setRodColor={setRodColor}
                          />
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Moulding Fields */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Moulding
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        {/* Top Finish Toggle */}
                        <Grid item xs={12}>
                          <YesNoRadio
                            label="Top Finish"
                            value={noTopFinish}
                            onChange={(val) => setNoTopFinish(val)}
                          />
                        </Grid>

                        {/* Top Moulding Style */}
                        {noTopFinish && (
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel id="moulding-style-label">
                                Top Moulding Style
                              </InputLabel>
                              <Select
                                labelId="moulding-style-label"
                                value={mouldingStyle}
                                onChange={(e) =>
                                  setMouldingStyle(e.target.value)
                                }
                                menuItems={moldingMenuItems}
                                label="Top Moulding Style"
                                error={!mouldingStyle}
                              >
                                {topoptions
                                  .filter((option) =>
                                    moldingMenuItems.noTopFinish.includes(
                                      option
                                    )
                                  )
                                  .map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {/* Bottom Finish Toggle */}
                        <Grid item xs={12}>
                          <YesNoRadio
                            label="Bottom Finish"
                            value={nobottomFinish}
                            onChange={(val) => setNoBottomFinish(val)}
                          />
                        </Grid>

                        {/* Bottom Moulding Style */}
                        {nobottomFinish && (
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel id="moulding1-style-label">
                                Bottom Moulding Style
                              </InputLabel>
                              <Select
                                labelId="moulding1-style-label"
                                value={moulding1Style}
                                onChange={(e) =>
                                  setMoulding1Style(e.target.value)
                                }
                                menuItems={moldingMenuItems}
                                label="Bottom Moulding Style"
                                error={!moulding1Style}
                              >
                                {bottomoptions
                                  .filter((option) =>
                                    moldingMenuItems.noBottomFinish.includes(
                                      option
                                    )
                                  )
                                  .map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Client's Existing Baseboard Section */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Client's Existing Baseboard
                      </Typography>
                      <Grid item xs={12}>
                        <ToggleButtonGroup
                          value={
                            baseboardRemove
                              ? "remove"
                              : baseboardNotch
                                ? "notch"
                                : ""
                          }
                          exclusive
                          onChange={(e, newValue) => {
                            if (newValue === "remove") {
                              setBaseboardRemove(true);
                              setBaseboardNotch(false);
                            } else if (newValue === "notch") {
                              setBaseboardRemove(false);
                              setBaseboardNotch(true);
                            } else {
                              setBaseboardRemove(false);
                              setBaseboardNotch(false);
                            }
                          }}
                          fullWidth
                          color="primary"
                        >
                          <ToggleButton value="remove" sx={{ flex: 1 }}>
                            REMOVE
                          </ToggleButton>
                          <ToggleButton value="notch" sx={{ flex: 1 }}>
                            NOTCH
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Is there backing? */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Is there backing?
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <YesNoRadio
                            value={backing}
                            onChange={(val) => setBacking(val)}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Is there drilling? */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              isBaseboardSectionComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Is there stop drilling?
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <RadioGroup
                              row
                              value={drilling}
                              onChange={(e) => {
                                const value = e.target.value;
                                setDrilling(value);
                                if (value !== "Custom") {
                                  setDrillingtxt("");
                                }
                              }}
                            >
                              <FormControlLabel
                                value="Standard"
                                control={<Radio />}
                                label="Standard"
                              />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                              <FormControlLabel
                                value="Custom"
                                control={<Radio />}
                                label="Custom"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            size="small"
                            style={{ display: drilling === "Custom" ? "block" : "none" }}
                          >
                            <FormLabel>Drilling Notes</FormLabel>
                            <TextField
                              inputRef={drillingChangeRef}
                              variant="outlined"
                              value={drillingtxt}
                              error={drillingNotesError}
                              onChange={(e) => {
                                const text = e.target.value;
                                if (text.length <= 500) {
                                  handleDrillingChangeNotes(e);
                                  if (drillingNotesError) setDrillingNotesError(false);
                                } else {
                                  notify("Character limit exceeded! Maximum 500 characters allowed for drilling notes.", "warning");
                                }
                              }}
                              multiline
                              rows={3}
                              fullWidth
                              helperText={
                                drillingNotesError
                                  ? "Drilling notes are required for custom drilling."
                                  : `${drillingtxt.length}/500 characters`
                              }
                            />

                          </FormControl>
                        </Grid>

                      </Grid>

                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Counter Top*/}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Countertop
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <YesNoRadio
                            value={countertopEnabled}
                            onChange={(val) => setCountertopEnabled(val)}
                          />
                        </Grid>
                      </Grid>
                      {countertopEnabled && (
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <CountertopOptions
                            countertopType={countertopType}
                            setCountertopType={setCountertopType}
                            countertopColor={countertopColor}
                            setCountertopColor={setCountertopColor}
                            countertopCollection={countertopCollection}
                            setCountertopCollection={setCountertopCollection}
                          />
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Tear Out Section */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              isCountertopComplete &&
              kitchenSpacesEnabled === false && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Tear Out
                      </Typography>
                      <Grid container spacing={2}>
                        {tearoutfields.map((field) => (
                          <Grid item xs={12} sm={6} md={6} key={field.key}>
                            <FormControl
                              fullWidth
                              required
                              size="small"
                              error={touched[field.key] && field.error}
                            >
                              <FormLabel>{field.label}</FormLabel>
                              <Select
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={() => handleBlur(field.key)}
                                variant="outlined"
                                sx={{ height: 54 }}
                              >
                                {field.options.map((option, index) => (
                                  <MenuItem key={index} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                              {touched[field.key] && field.error && (
                                <Typography variant="caption" color="error">
                                  ⚠️ {field.helperText}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Rack Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              isCountertopComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Racks
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <YesNoRadio
                            value={rackEnabled}
                            onChange={(val) => {
                              setRackEnabled(val);
                              setRackLists([]);
                            }}
                          />
                        </Grid>
                      </Grid>
                      {rackEnabled && (
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12}>
                            <DynamicTabsForm
                              rows={rackLists}
                              onRowsChange={setRackLists}
                              activeTab={activeRackTab}
                              onActiveTabChange={setActiveRackTab}
                              labelPrefix="Rack"
                              createEmptyRow={createEmptyRackRow}
                              renderRow={(row, index, onFieldChange) => (
                                <RackRowFields
                                  row={row}
                                  index={index}
                                  onFieldChange={(idx, patch) => {
                                    onFieldChange(idx, patch);
                                    // Clear any error flags for fields the user just edited
                                    setRackFieldErrors((prev) => {
                                      const next = { ...prev };
                                      Object.keys(patch).forEach((key) => delete next[`${idx}-${key}`]);
                                      return next;
                                    });
                                  }}
                                  fieldErrors={rackFieldErrors}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Basket Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              isCountertopComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Baskets
                      </Typography>
                      <Grid2 container spacing={2} alignItems="center">
                        <Grid2 item xs={6}>
                          <YesNoRadio
                            value={basketEnabled}
                            onChange={(val) => {
                              setBasketEnabled(val);
                              setBasketLists([]);
                            }}
                          />
                        </Grid2>
                      </Grid2>
                      {basketEnabled && (
                        <Grid2 container spacing={2} alignItems="center">
                          <Grid2 item xs={12}>
                            <DynamicTabsForm
                              rows={basketLists}
                              onRowsChange={setBasketLists}
                              activeTab={activeBasketTab}
                              onActiveTabChange={setActiveBasketTab}
                              labelPrefix="Basket"
                              createEmptyRow={createEmptyBasketRow}
                              renderRow={(row, index, onFieldChange) => (
                                <BasketRowFields
                                  row={row}
                                  index={index}
                                  onFieldChange={(idx, patch) => {
                                    onFieldChange(idx, patch);
                                    setBasketFieldErrors((prev) => {
                                      const next = { ...prev };
                                      Object.keys(patch).forEach((key) => delete next[`${idx}-${key}`]);
                                      return next;
                                    });
                                  }}
                                  fieldErrors={basketFieldErrors}
                                />
                              )}
                            />
                          </Grid2>
                        </Grid2>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Hooks Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              isCountertopComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Hooks
                      </Typography>
                      <Grid2 container spacing={2} alignItems="center">
                        <Grid2 item xs={6}>
                          <YesNoRadio
                            value={hooksEnabled}
                            onChange={(val) => {
                              setHooksEnabled(val);
                              setHooksLists([]);
                            }}
                          />
                        </Grid2>
                      </Grid2>
                      {hooksEnabled && (
                        <Grid2 container spacing={2} alignItems="center">
                          <Grid2 item xs={12}>
                            <DynamicTabsForm
                              rows={hooksLists}
                              onRowsChange={setHooksLists}
                              activeTab={activeHooksTab}
                              onActiveTabChange={setActiveHooksTab}
                              labelPrefix="Hook"
                              createEmptyRow={createEmptyHookRow}
                              renderRow={(row, index, onFieldChange) => (
                                <HookRowFields
                                  row={row}
                                  index={index}
                                  onFieldChange={(idx, patch) => {
                                    onFieldChange(idx, patch);
                                    setHookFieldErrors((prev) => {
                                      const next = { ...prev };
                                      Object.keys(patch).forEach((key) => delete next[`${idx}-${key}`]);
                                      return next;
                                    });
                                  }}
                                  fieldErrors={hookFieldErrors}
                                />
                              )}
                            />
                          </Grid2>
                        </Grid2>

                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Hamper Fields Card - Visible if Collection Fields complete */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              ismouldingComplete &&
              isBaseboardSectionComplete &&
              isCountertopComplete && (
                <Grid2 item xs={12} width="100%">
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Hampers
                      </Typography>
                      <Grid2 container spacing={2} alignItems="center">
                        <Grid2 item xs={6}>
                          <YesNoRadio
                            value={hamperEnabled}
                            onChange={(val) => {
                              setHamperEnabled(val);
                              setHamperLists([]);
                            }}
                          />
                        </Grid2>
                      </Grid2>
                      {hamperEnabled && (
                        <Grid2 container spacing={2} alignItems="center">
                          <Grid2 item xs={12}>
                            <DynamicTabsForm
                              rows={hamperLists}
                              onRowsChange={setHamperLists}
                              activeTab={activeHamperTab}
                              onActiveTabChange={setActiveHamperTab}
                              labelPrefix="Hamper"
                              createEmptyRow={createEmptyHamperRow}
                              renderRow={(row, index, onFieldChange) => (
                                <HamperRowFields
                                  row={row}
                                  index={index}
                                  onFieldChange={(idx, patch) => {
                                    onFieldChange(idx, patch);
                                    setHamperFieldErrors((prev) => {
                                      const next = { ...prev };
                                      Object.keys(patch).forEach((key) => delete next[`${idx}-${key}`]);
                                      return next;
                                    });
                                  }}
                                  fieldErrors={hamperFieldErrors}
                                />
                              )}
                            />
                          </Grid2>
                        </Grid2>
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid2>
              )}

            {/* Add TextField  */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              isBillingAddressComplete &&
              isDesignatedCuttingAreaComplete &&
              isSection2Complete &&
              isSection3Complete &&
              specialDoorFieldsFilled &&
              isSection4Complete &&
              isSection5Complete &&
              isBaseboardSectionComplete && (
                <Grid2 item xs={12} width="100%">
                  <Card
                    //variant="outlined"
                    sx={{
                      width: "95%",
                      position: "relative",
                      //borderRadius: 2,
                      //boxShadow: 3,
                      backgroundColor: "#faf9f7",
                      //border: "2px solid #352c1d",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Additional Information
                      </Typography>
                      <Grid container spacing={2}>
                        {additionalDetails.map((field) => (
                          <Grid item xs={12} sm={12} md={12} key={field.key}>
                            <FormControl
                              fullWidth
                              size="small"
                              error={touched[field.key] && field.error}
                            >
                              <FormLabel>{field.label}</FormLabel>
                              <TextField
                                variant="outlined"
                                type={field.type}
                                value={field.value}
                                onChange={(e) =>
                                  field.onChange
                                    ? field.onChange(e)
                                    : field.setValue(e.target.value)
                                }
                                onBlur={() => handleBlur(field.key)}
                                inputProps={field.inputProps}
                                helperText={
                                  touched[field.key] && field.error
                                    ? field.helperText
                                    : ""
                                }
                                multiline
                              />
                            </FormControl>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid2>
              )}

            {/* adding button to create closets form and then show add to Send For Sign button */}
            {isSection1Complete &&
              isAddressSectionComplete &&
              /*isBillingAddressComplete &&*/ (
                <Grid2
                  item
                  xs={12}
                  width="100%"
                  sx={{
                    display: "flex",
                    justifyContent: "center", // center the Card horizontally in the Grid
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleClosetsFormSubmit(!!jobIdValue)}
                    sx={{
                      borderRadius: 2,
                      boxShadow: 2,
                      backgroundColor: "#352c1d",
                      color: "#fff",
                    }}
                  >
                    {!!jobIdValue ? "Update" : "Save"}
                  </Button>
                </Grid2>
              )}
            {visibleFinancialSummary && (
              <Grid2 item xs={12} width="100%">
                <Card
                  //variant="outlined"
                  sx={{
                    width: "95%",
                    position: "relative",
                    //borderRadius: 2,
                    //boxShadow: 3,
                    backgroundColor: "#faf9f7",
                    //border: "2px solid #352c1d",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Upload File
                    </Typography>
                    <Grid2 container spacing={2}>
                      <Grid2 item xs={12} sm={12} md={12}>
                        <FormControl
                          fullWidth
                          size="medium"
                          sx={{ padding: "10px" }}
                        >
                          <FormLabel sx={{ padding: "5px" }}>
                            Design Sheet
                          </FormLabel>
                          <input
                            type="file"
                            name="designsheet"
                            onChange={(e) => handleDesignSheetEvent(e)}
                            accept="application/pdf"
                          ></input>
                          {designFileURL && (
                            <Link
                              href={designFileURL}
                              underline="none"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                marginTop: "10px",
                                backgroundColor: "#faf9f7",
                                color: "black",
                                border: "2px solid #352c1d",
                                padding: "10px 20px",
                                textAlign: "center",
                                display: "inline-block",
                                width: "100px",
                                "&:hover, &:active": {
                                  backgroundColor: "#352c1d",
                                  color: "white",
                                },
                              }}
                            >
                              Design File
                            </Link>
                          )}
                        </FormControl>
                        <FormControl
                          fullWidth
                          size="medium"
                          sx={{ padding: "10px" }}
                        >
                          <FormLabel sx={{ padding: "5px" }}>
                            Price Presentation
                          </FormLabel>
                          <input
                            type="file"
                            name="pricepresentation"
                            onChange={(e) => handlePricePresentationEvent(e)}
                            accept="application/pdf"
                          ></input>
                          {pricePresentationFileURL && (
                            <Link
                              href={pricePresentationFileURL}
                              underline="none"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                marginTop: "10px",
                                backgroundColor: "#faf9f7",
                                color: "black",
                                border: "2px solid #352c1d",
                                padding: "10px 20px",
                                textAlign: "center",
                                display: "inline-block",
                                width: "120px",
                                "&:hover, &:active": {
                                  backgroundColor: "#352c1d",
                                  color: "white",
                                },
                              }}
                            >
                              Presentation File
                            </Link>
                          )}
                        </FormControl>
                      </Grid2>
                    </Grid2>
                  </CardContent>
                </Card>
              </Grid2>
            )}

            {/* Add Payment  */}
            {visibleFinancialSummary && (
              <Grid2 item xs={12} width="100%">
                <Card
                  sx={{
                    width: "95%",
                    position: "relative",
                    backgroundColor: "#faf9f7",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Upload File
                    </Typography>
                    <Grid2 container spacing={2}>
                      <Grid2 item xs={12} sm={12} md={12}>
                        <FormControl fullWidth size="medium" sx={{ padding: "10px" }}>
                          <FormLabel sx={{ padding: "5px" }}>Design Sheet</FormLabel>
                          <input
                            type="file"
                            name="designsheet"
                            onChange={(e) => handleDesignSheetEvent(e)}
                            accept="application/pdf"
                          ></input>
                          {designFileURL && (
                            <Link
                              href={designFileURL}
                              underline="none"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                marginTop: "10px",
                                backgroundColor: "#faf9f7",
                                color: "black",
                                border: "2px solid #352c1d",
                                padding: "10px 20px",
                                textAlign: "center",
                                display: "inline-block",
                                width: "100px",
                                "&:hover, &:active": {
                                  backgroundColor: "#352c1d",
                                  color: "white",
                                },
                              }}
                            >
                              Design File
                            </Link>
                          )}
                        </FormControl>
                        <FormControl fullWidth size="medium" sx={{ padding: "10px" }}>
                          <FormLabel sx={{ padding: "5px" }}>Price Presentation</FormLabel>
                          <input
                            type="file"
                            name="pricepresentation"
                            onChange={(e) => handlePricePresentationEvent(e)}
                            accept="application/pdf"
                          ></input>
                          {pricePresentationFileURL && (
                            <Link
                              href={pricePresentationFileURL}
                              underline="none"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                marginTop: "10px",
                                backgroundColor: "#faf9f7",
                                color: "black",
                                border: "2px solid #352c1d",
                                padding: "10px 20px",
                                textAlign: "center",
                                display: "inline-block",
                                width: "120px",
                                "&:hover, &:active": {
                                  backgroundColor: "#352c1d",
                                  color: "white",
                                },
                              }}
                            >
                              Presentation File
                            </Link>
                          )}
                        </FormControl>
                      </Grid2>
                    </Grid2>
                  </CardContent>
                </Card>
              </Grid2>
            )}

            {/* Add Payment  */}
            {visibleFinancialSummary && (
              <Grid2 container spacing={2} direction="column" sx={{ width: "100%" }}>
                {financeBlocksToRender.map((block) => (
                  <React.Fragment key={block.key}>
                    <Grid2 item xs={12} width="100%">
                      <Card sx={{ width: "95%", position: "relative", backgroundColor: "#faf9f7" }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {block.title} Financial Summary
                          </Typography>
                          <Grid container spacing={2} sx={{ mt: -1 }}>
                            <Grid item xs={6}>
                              <YesNoRadio
                                label="Do you have Status Card?"
                                value={statusCard}
                                onChange={(val) => {
                                  setStatusCard(val);
                                  if (!val) setStatusCardNumber("");
                                }}
                              />
                            </Grid>
                            {statusCard && (
                              <Grid item xs={12} sm={6} md={6}>
                                <FormControl fullWidth size="small">
                                  <FormLabel>Status Card Number</FormLabel>
                                  <TextField
                                    variant="outlined"
                                    type="text"
                                    value={statusCardNumber}
                                    onChange={(e) => setStatusCardNumber(e.target.value)}
                                    placeholder="Enter Status Card Number"
                                  />
                                </FormControl>
                              </Grid>
                            )}
                          </Grid>
                          <Grid container spacing={2}>
                            {block.financialFields.map((field) => (
                              <Grid item xs={12} sm={6} md={6} key={field.key}>
                                <FormControl fullWidth required size="small" error={touched[field.key] && field.error}>
                                  <FormLabel>{field.label}</FormLabel>
                                  <TextField
                                    variant="outlined"
                                    type={field.type}
                                    value={field.value}
                                    onChange={(e) => field.onChange(e)}
                                    onBlur={() => handleBlur(field.key)}
                                    inputProps={field.inputProps}
                                    disabled={field.key.endsWith("_hst")}
                                    helperText={touched[field.key] && field.error ? errorPrefix + field.helperText : ""}
                                  />
                                </FormControl>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid2>

                    <Grid2 item xs={12} width="100%">
                      <Card sx={{ width: "95%", position: "relative", backgroundColor: "#faf9f7" }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {block.title} Payment Details
                          </Typography>
                          <Grid container spacing={2}>
                            {block.paymentFields.map((field) =>
                              field.showIf === undefined || field.showIf === true ? (
                                <Grid item xs={12} sm={6} md={6} key={field.key}>
                                  <FormControl fullWidth required size="small" error={touched[field.key] && field.error}>
                                    <FormLabel>{field.label}</FormLabel>
                                    {field.type === "select" ? (
                                      <Select
                                        value={field.value}
                                        onChange={field.onChange}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ height: 54 }}
                                      >
                                        {field.options.map((option, index) => (
                                          <MenuItem key={index} value={option}>{option}</MenuItem>
                                        ))}
                                      </Select>
                                    ) : (
                                      <TextField
                                        variant="outlined"
                                        type={field.type}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={() => handleBlur(field.key)}
                                        inputProps={field.inputProps}
                                        helperText={touched[field.key] && field.error ? errorPrefix + field.helperText : ""}
                                      />
                                    )}
                                  </FormControl>
                                </Grid>
                              ) : null
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid2>
                  </React.Fragment>
                ))}

                <Grid2 item xs={12} width="100%">
                  <CardContent
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        handlePaymentDetails();
                        setPaymentDetailsAdded(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        backgroundColor: "#352c1d",
                        color: "#fff",
                        display: isSpaceFlowComplete ? "block" : "none",
                      }}
                    >
                      Save Payment Details
                    </Button>
                  </CardContent>
                </Grid2>

                {paymentDetailsAdded && (
                  <Grid2 item xs={12} width="100%">
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        onClick={() => handleSubmit(true)}
                        disabled={!isFormComplete}
                        variant="outlined"
                        color="primary"
                        sx={{
                          borderRadius: 2,
                          boxShadow: 3,
                          margin: "10px",
                        }}
                      >
                        Preview PDF
                      </Button>
                      {hasSeenPreview && (
                        <Button
                          onClick={async () => {
                            setLoading(true);
                            try {
                              await getJobIdDetail(jobIdValue);
                              setShouldProcessEvent(true);
                            } catch (err) {
                              logger.error("Send for sign error:", err);
                              setLoading(false);
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={!isFormComplete || Loading}
                          variant="contained"
                          color="primary"
                          sx={{
                            borderRadius: 2,
                            boxShadow: 3,
                            margin: "10px",
                            display: "flex",
                          }}
                        >
                          {Loading ? (
                            <CircularProgress size={24} color="inherit" sx={{ width: "200" }} />
                          ) : (
                            "Send for sign"
                          )}
                        </Button>
                      )}
                    </CardContent>
                  </Grid2>
                )}
              </Grid2>
            )}
          </Grid2>

        </form>
        {/* Floating Buttons for PDF Actions */}
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >

        </Box>
        <DraggableHome />
      </AppTheme>
    </Box>

  );
};
export default Home;